import React, { Component } from 'react';
import {
    FiSave,
    FiRefreshCw,
    FiX,
    FiLayout,
    FiDroplet,
    FiClock,
    FiChevronRight,
    FiChevronDown,
    FiCheck,
    FiInfo,
    FiLayers,
    FiType,
    FiAlertCircle,
    FiHelpCircle,
    FiTrash2,
    FiCopy,
    FiMousePointer,
    FiShare2,
    FiUsers
} from 'react-icons/fi';
import {
    SimpleDesignConfig,
    defaultDesignConfig,
    tabStyles,
    fontFamilies,
    TabLevelColors,
    TabLevelName,
    tabLevelLabels,
    defaultTabLevelColors,
    syncAccordionToHeader,
    parseLegacyCSSToConfig,
    hasValidCSSVariables
} from '../SimpleDesignConfig';
import './DesignConfigurator.css';
import logoAufbau from '../assets/ksl-logo-animiert.svg';
import { DesignConfiguratorSA } from './DesignConfiguratorSA';
import * as KSLPlusAPI from '../services/KSLPlusAPI';
import { env } from '../env';
import { getTabStyleCSS, generateTabColorsCSS, styleUsesBorderColor } from '../TabStyleGenerator';
import { ColorPicker } from 'material-ui-color';


// Lade-Overlay mit Logo-Aufbau-Animation beim Öffnen des Designers
// Aufbau ist bei ~4,9 s fertig (Ladebalken voll); danach bleibt der Endzustand ~1 s stehen, dann öffnet der Designer
const LOADING_MIN_DISPLAY_MS = 6000;
const LOADING_FADE_OUT_MS = 500;       // Ausblend-Dauer des Overlays

interface Props {
    kunde: number;
    gemeinde: number;
    jahr: number;
    verwaltungsansicht: boolean;
    onClose: () => void;
    onSave: (css: string) => Promise<void>;
    onActivate?: () => Promise<void>;
    children: React.ReactNode;
    hasHauptText: boolean;
    // Rollen des angemeldeten Nutzers (für das Superadmin-Übertragungs-Tool)
    userRoles: string[];
    // Auth-Token ("Bearer ...") für die rollengeschützten Endpoints
    authToken: string;
}

interface State {
    config: SimpleDesignConfig;
    originalConfig: SimpleDesignConfig;
    workingConfig: SimpleDesignConfig;
    activeTab: string;
    isSaving: boolean;
    isLoading: boolean;
    error: string | null;
    successMessage: string | null;
    designHistory: DesignVersionInfo[];
    designTemplates: DesignTemplateResponse[];
    isLoadingHistory: boolean;
    isLoadingTemplates: boolean;
    availableVersions: DesignVersionInfo[];
    currentLiveVersion: number | null;
    isLoadingVersions: boolean;
    selectedVersion: number | null;
    selectedTemplateId: number | null;
    previewConfig: SimpleDesignConfig | null;
    pendingAction: 'save' | 'saveAndActivate' | 'reset' | 'activateVersion' | 'applyTemplate' | 'deleteVersion' | 'transferDesign' | null;
    pendingActionData: any;
    isDraftLoaded: boolean;
    expandedTabLevel: TabLevelName | null;
    hasLegacyCSS: boolean;
    legacyCSSText: string | null;
    // Übertragung des Live-Designs auf andere Gemeinden des Kunden
    customerGemeinden: TransferGemeinde[];
    selectedTransferGemeinden: number[];
    isLoadingGemeinden: boolean;
    isTransferring: boolean;
    transferDropdownOpen: boolean;
    // Superadmin: Öffnen-Zustand des kundenübergreifenden Zuordnungsdialogs
    externDialogOpen: boolean;
    // Lade-Overlay mit Logo-Aufbau-Animation
    loadingVisible: boolean;
    loadingFadeOut: boolean;
    loadingMinElapsed: boolean;
}

// versions-response vom backend
interface DesignVersionInfo {
    version: number;
    erstelltAm: string;
    geaendertAm: string;
    geaendertVon?: string;
    isCurrent: boolean;
    istLive?: boolean;
    konfigurationJson: string;
}

interface DesignTemplateResponse {
    id: number;
    name: string;
    beschreibung?: string;
    kategorie?: string;
    tags?: string;
    vorschaubild?: string;
    konfigurationJson: string;
}

// Gemeinde des Kunden, auf die das Live-Design übertragen werden kann
interface TransferGemeinde {
    gemeinde: number;
    name: string;
}

export class DesignConfigurator extends Component<Props, State> {

    private styleElementId = 'design-preview-styles';
    private draftSaveTimeout: ReturnType<typeof setTimeout> | null = null;
    private cssUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
    private isSavingDraft: boolean = false;
    private loadingMinTimer: ReturnType<typeof setTimeout> | null = null;
    private loadingFadeTimer: ReturnType<typeof setTimeout> | null = null;
    // Cache-Bust pro Mount: animierte SVGs im <img> behalten sonst ihre Animations-Zeitleiste
    // aus dem Browser-Cache und zeigen bei F5/Reload nur den eingefrorenen Endzustand,
    // statt den Aufbau neu abzuspielen
    private logoSrc = `${logoAufbau}?v=${Date.now()}`;

    constructor(props: Props) {
        super(props);

        this.state = {
            config: { ...defaultDesignConfig },
            originalConfig: { ...defaultDesignConfig },
            workingConfig: { ...defaultDesignConfig },
            activeTab: 'header',
            isSaving: false,
            isLoading: false,
            error: null,
            successMessage: null,
            designHistory: [],
            designTemplates: [],
            isLoadingHistory: false,
            isLoadingTemplates: false,
            availableVersions: [],
            currentLiveVersion: null,
            isLoadingVersions: false,
            selectedVersion: null,
            selectedTemplateId: null,
            previewConfig: null,
            pendingAction: null,
            pendingActionData: null,
            isDraftLoaded: false,
            expandedTabLevel: 'tabTop',
            hasLegacyCSS: false,
            legacyCSSText: null,
            customerGemeinden: [],
            selectedTransferGemeinden: [],
            isLoadingGemeinden: false,
            isTransferring: false,
            transferDropdownOpen: false,
            externDialogOpen: false,
            loadingVisible: true,
            loadingFadeOut: false,
            loadingMinElapsed: false,
        };
    }

    // lifecycle

    componentDidMount() {
        this.loadDesignTemplates();
        this.loadDesignHistory();
        this.loadAvailableVersions();
        this.loadCustomerGemeinden();
        this.initializeConfigurator();

        // Lade-Overlay mindestens so lange zeigen, dass die Aufbau-Animation sichtbar ist
        this.loadingMinTimer = setTimeout(() => {
            this.setState({ loadingMinElapsed: true }, this.maybeFinishLoadingOverlay);
        }, LOADING_MIN_DISPLAY_MS);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevState.config !== this.state.config) {
            // Debounced CSS-Update: Verhindert exzessive Neuberechnungen bei schnellen Änderungen (z.B. Color-Picker)
            this.scheduleCSSUpdate();
        }
        if (prevState.workingConfig !== this.state.workingConfig) {
            this.scheduleDraftSave();
        }
        // Overlay ausblenden, sobald Konfiguration geladen und Mindestzeit erreicht ist
        this.maybeFinishLoadingOverlay();
    }

    componentWillUnmount() {
        this.removePreviewStyles();
        if (this.draftSaveTimeout) {
            clearTimeout(this.draftSaveTimeout);
        }
        if (this.cssUpdateTimeout) {
            clearTimeout(this.cssUpdateTimeout);
        }
        if (this.loadingMinTimer) {
            clearTimeout(this.loadingMinTimer);
        }
        if (this.loadingFadeTimer) {
            clearTimeout(this.loadingFadeTimer);
        }
    }

    // Blendet das Lade-Overlay aus, sobald die Konfiguration steht und die
    // Mindestanzeigedauer erreicht ist. Designer und Overlay werden dabei
    // gleichzeitig „aufgedeckt".
    maybeFinishLoadingOverlay = () => {
        if (!this.state.loadingVisible || this.state.loadingFadeOut) {
            return;
        }
        if (this.state.isLoading || !this.state.loadingMinElapsed) {
            return;
        }

        this.setState({ loadingFadeOut: true });
        this.loadingFadeTimer = setTimeout(() => {
            this.setState({ loadingVisible: false });
        }, LOADING_FADE_OUT_MS);
    }

    // Debounced CSS-Update (50ms) - verhindert Memory-Probleme bei schnellen Änderungen
    scheduleCSSUpdate = () => {
        if (this.cssUpdateTimeout) {
            clearTimeout(this.cssUpdateTimeout);
        }
        this.cssUpdateTimeout = setTimeout(() => {
            this.updateLiveCSS();
        }, 50);
    }

    /**
 * Lädt Legacy-CSS aus dbo.Texte (Plantyp 3).
 * Gibt das CSS als String zurück oder null wenn nicht vorhanden.
 */
    loadLegacyCSS = async (): Promise<string | null> => {
        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const client = new KSLPlusAPI.DigitalHoushold_Client(api);

            // Plantyp 3 = Legacy-CSS in dbo.Texte
            const texte = await client.texte(
                this.props.kunde,
                this.props.gemeinde,
                this.props.jahr,
                3,  // Plantyp für CSS
                ""
            );

            if (texte && texte.length > 0 && texte[0].text && texte[0].text.length > 0) {
                console.log('[DesignConfigurator] Legacy-CSS gefunden, Länge:', texte[0].text.length);
                return texte[0].text;
            }

            console.log('[DesignConfigurator] Kein Legacy-CSS in dbo.Texte vorhanden');
            return null;

        } catch (error) {
            console.log('[DesignConfigurator] Fehler beim Laden von Legacy-CSS:', error);
            return null;
        }
    }

    /**
 * Lädt die Original-Konfiguration für den Reset-Button.
 * Hierarchie: Legacy-CSS → Standard-Design (defaultDesignConfig)
 * 
 */
    loadOriginalConfig = async (): Promise<void> => {
        console.log('[DesignConfigurator] Lade Original-Konfiguration für Reset...');

        // Prüfe auf Legacy-CSS aus dbo.Texte
        const legacyCSS = await this.loadLegacyCSS();

        if (legacyCSS && hasValidCSSVariables(legacyCSS)) {
            // Legacy-CSS gefunden → Konvertiere zu SimpleDesignConfig
            console.log('[DesignConfigurator] Verwende Legacy-CSS als Original für Reset');

            const legacyConfig = parseLegacyCSSToConfig(legacyCSS);

            this.setState({
                originalConfig: JSON.parse(JSON.stringify(legacyConfig)),
                hasLegacyCSS: true,
                legacyCSSText: legacyCSS
            });

            return;
        }

        // Kein Legacy-CSS → Standard-Design verwenden
        console.log('[DesignConfigurator] Kein Legacy-CSS, verwende Standard-Design als Original');

        this.setState({
            originalConfig: JSON.parse(JSON.stringify(defaultDesignConfig)),
            hasLegacyCSS: false,
            legacyCSSText: null
        });
    }

    /**
 * Initialisiert den Konfigurator in der korrekten Reihenfolge:
 * 1. Original-Config laden (für Reset)
 * 2. Draft oder aktuelle Config laden (für Bearbeitung)
 */
    initializeConfigurator = async (): Promise<void> => {
        this.setState({ isLoading: true });

        try {
            // Schritt 1: Original für Reset laden (Legacy-CSS oder Standard)
            await this.loadOriginalConfig();

            // Schritt 2: Draft oder aktuelle Bearbeitung laden
            await this.loadDraft();

        } catch (error) {
            console.error('[DesignConfigurator] Fehler bei Initialisierung:', error);
            this.setState({
                error: 'Fehler beim Laden der Konfiguration',
                isLoading: false
            });
        }
    }

    // ============================================================
    // DRAFT (ARBEITSVERSION) MANAGEMENT
    // ============================================================

    // Verzögertes Speichern des Drafts (Debounce 1 Sekunde)
    scheduleDraftSave = () => {
        if (this.draftSaveTimeout) {
            clearTimeout(this.draftSaveTimeout);
        }
        this.draftSaveTimeout = setTimeout(() => {
            this.saveDraft();
        }, 1000);
    }

    // Lädt die Arbeitsversion aus der Datenbank
    loadDraft = async () => {
        this.setState({ isLoading: true });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const designClient = new KSLPlusAPI.Design_Client(api);

            const response = await designClient.draftGet(
                this.props.kunde,
                this.props.gemeinde,
                this.props.jahr,
                1,
                undefined
            );

            if (response && response.konfiguration) {
                const loadedConfig = this.parseConfig(response.konfiguration);

                this.setState({
                    config: loadedConfig,
                    workingConfig: JSON.parse(JSON.stringify(loadedConfig)),
                    isDraftLoaded: true,
                    isLoading: false
                }, () => {
                    this.updateLiveCSS();
                });
            } else {
                this.setState({ isDraftLoaded: true });
                this.loadExistingConfig();
            }
        } catch (error) {
            this.setState({ isDraftLoaded: true });
            this.loadExistingConfig();
        }
    }

    // Speichert die aktuelle Arbeitsversion als Draft
    // Mit Sperre gegen parallele Aufrufe zur Vermeidung von Race Conditions
    saveDraft = async () => {
        // Verhindere parallele Draft-Saves
        if (this.isSavingDraft) {
            return;
        }

        this.isSavingDraft = true;
        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const designClient = new KSLPlusAPI.Design_Client(api);

            const request = new KSLPlusAPI.SaveDesignConfigRequest();
            request.kunde = this.props.kunde;
            request.gemeinde = this.props.gemeinde;
            request.jahr = this.props.jahr;
            request.ansicht = 1;
            request.konfiguration = this.createConfigModelFrom(this.state.workingConfig);

            await designClient.draftPut(request);
        } catch (error) {
            console.error('Fehler beim Speichern des Drafts:', error);
        } finally {
            this.isSavingDraft = false;
        }
    }

    // Erstellt ein ConfigModel aus einer beliebigen SimpleDesignConfig
    createConfigModelFrom = (config: SimpleDesignConfig): KSLPlusAPI.DesignConfigModel => {
        const configModel = new KSLPlusAPI.DesignConfigModel();

        // === ALLGEMEIN ===
        configModel.websiteBackgroundColor = config.websiteBackgroundColor;
        configModel.fontFamily = config.fontFamily;

        // === SCHRIFTEN ===
        configModel.textColor = config.textColor;
        configModel.headingColor = config.headingColor;
        configModel.headingBold = config.headingBold;

        // === HEADER ===
        configModel.headerBgColor = config.headerBgColor;
        configModel.headerHeight = config.headerHeight;
        configModel.headerGradientEnabled = config.headerGradientEnabled;
        configModel.headerGradientColor = config.headerGradientColor;
        configModel.headerGradientAngle = config.headerGradientAngle;
        configModel.headerGradientToWhite = config.headerGradientToWhite;
        configModel.headlineMainColor = config.headlineMainColor;
        configModel.headlineSecondColor = config.headlineSecondColor;

        // === TABS - EINHEITLICHER STYLE (NEU: ohne "Top" im Namen) ===
        configModel.tabStyle = config.tabStyle;
        configModel.tabBorderColor = config.tabBorderColor;

        // === TABS - EBENE 1 (Top) ===
        configModel.tabTop = new KSLPlusAPI.TabLevelColors();
        configModel.tabTop.activeColor = config.tabTop.activeColor;
        configModel.tabTop.activeBgColor = config.tabTop.activeBgColor;
        configModel.tabTop.inactiveColor = config.tabTop.inactiveColor;
        configModel.tabTop.inactiveBgColor = config.tabTop.inactiveBgColor;
        configModel.tabTop.hoverColor = config.tabTop.hoverColor;
        configModel.tabTop.hoverBgColor = config.tabTop.hoverBgColor;

        // === TABS - EBENE 2 (Middle) ===
        configModel.tabMiddle = new KSLPlusAPI.TabLevelColors();
        configModel.tabMiddle.activeColor = config.tabMiddle.activeColor;
        configModel.tabMiddle.activeBgColor = config.tabMiddle.activeBgColor;
        configModel.tabMiddle.inactiveColor = config.tabMiddle.inactiveColor;
        configModel.tabMiddle.inactiveBgColor = config.tabMiddle.inactiveBgColor;
        configModel.tabMiddle.hoverColor = config.tabMiddle.hoverColor;
        configModel.tabMiddle.hoverBgColor = config.tabMiddle.hoverBgColor;

        // === TABS - EBENE 3 (Bottom) ===
        configModel.tabBottom = new KSLPlusAPI.TabLevelColors();
        configModel.tabBottom.activeColor = config.tabBottom.activeColor;
        configModel.tabBottom.activeBgColor = config.tabBottom.activeBgColor;
        configModel.tabBottom.inactiveColor = config.tabBottom.inactiveColor;
        configModel.tabBottom.inactiveBgColor = config.tabBottom.inactiveBgColor;
        configModel.tabBottom.hoverColor = config.tabBottom.hoverColor;
        configModel.tabBottom.hoverBgColor = config.tabBottom.hoverBgColor;

        // === TABS - EBENE 4 (VeryBottom) ===
        configModel.tabVeryBottom = new KSLPlusAPI.TabLevelColors();
        configModel.tabVeryBottom.activeColor = config.tabVeryBottom.activeColor;
        configModel.tabVeryBottom.activeBgColor = config.tabVeryBottom.activeBgColor;
        configModel.tabVeryBottom.inactiveColor = config.tabVeryBottom.inactiveColor;
        configModel.tabVeryBottom.inactiveBgColor = config.tabVeryBottom.inactiveBgColor;
        configModel.tabVeryBottom.hoverColor = config.tabVeryBottom.hoverColor;
        configModel.tabVeryBottom.hoverBgColor = config.tabVeryBottom.hoverBgColor;

        // === ACCORDION ===
        configModel.accordionBgColor = config.accordionBgColor;
        configModel.accordionActiveBgColor = config.accordionActiveBgColor;
        configModel.accordionTextColor = config.accordionTextColor;
        configModel.accordionContentBgColor = config.accordionContentBgColor;
        configModel.accordionContentTextColor = config.accordionContentTextColor;
        configModel.accordionMatchHeader = config.accordionMatchHeader;

        // === BUTTONS ===
        configModel.buttonBgColor = config.buttonBgColor;
        configModel.buttonHoverColor = config.buttonHoverColor;
        configModel.buttonTextColor = config.buttonTextColor;

        return configModel;
    }

    // ============================================================
    // CONFIG PARSING
    // ============================================================

    // Parst Konfiguration - akzeptiert JSON-String ODER bereits geparstes Objekt
    parseConfig = (input: string | object): SimpleDesignConfig => {
        try {
            const defaultConfig = this.getDefaultConfig();
            const parsed = typeof input === 'string' ? JSON.parse(input) : input;

            // Migration: Alte flache Tab-Properties auf neue verschachtelte Struktur mappen
            let tabTop: TabLevelColors = { ...defaultTabLevelColors };
            let tabMiddle: TabLevelColors = { ...defaultTabLevelColors };
            let tabBottom: TabLevelColors = { ...defaultTabLevelColors };
            let tabVeryBottom: TabLevelColors = { ...defaultTabLevelColors };

            // Prüfen ob bereits verschachtelte Struktur vorhanden
            if (parsed.tabTop && typeof parsed.tabTop === 'object') {
                tabTop = { ...defaultTabLevelColors, ...parsed.tabTop };
            } else if (parsed.tabTopActiveColor !== undefined) {
                // Migration von flacher Struktur
                tabTop = {
                    activeColor: parsed.tabTopActiveColor || defaultTabLevelColors.activeColor,
                    inactiveColor: parsed.tabTopInactiveColor || defaultTabLevelColors.inactiveColor,
                    hoverColor: parsed.tabTopHoverColor || defaultTabLevelColors.hoverColor,
                    activeBgColor: parsed.tabTopActiveBgColor || defaultTabLevelColors.activeBgColor,
                    inactiveBgColor: parsed.tabTopInactiveBgColor || defaultTabLevelColors.inactiveBgColor,
                    hoverBgColor: parsed.tabTopHoverBgColor || defaultTabLevelColors.hoverBgColor
                };
            }

            if (parsed.tabMiddle && typeof parsed.tabMiddle === 'object') {
                tabMiddle = { ...defaultTabLevelColors, ...parsed.tabMiddle };
            } else if (parsed.tabMiddleActiveColor !== undefined) {
                tabMiddle = {
                    activeColor: parsed.tabMiddleActiveColor || defaultTabLevelColors.activeColor,
                    inactiveColor: parsed.tabMiddleInactiveColor || defaultTabLevelColors.inactiveColor,
                    hoverColor: parsed.tabMiddleHoverColor || defaultTabLevelColors.hoverColor,
                    activeBgColor: parsed.tabMiddleActiveBgColor || defaultTabLevelColors.activeBgColor,
                    inactiveBgColor: parsed.tabMiddleInactiveBgColor || defaultTabLevelColors.inactiveBgColor,
                    hoverBgColor: parsed.tabMiddleHoverBgColor || defaultTabLevelColors.hoverBgColor
                };
            }

            if (parsed.tabBottom && typeof parsed.tabBottom === 'object') {
                tabBottom = { ...defaultTabLevelColors, ...parsed.tabBottom };
            } else if (parsed.tabBottomActiveColor !== undefined) {
                tabBottom = {
                    activeColor: parsed.tabBottomActiveColor || defaultTabLevelColors.activeColor,
                    inactiveColor: parsed.tabBottomInactiveColor || defaultTabLevelColors.inactiveColor,
                    hoverColor: parsed.tabBottomHoverColor || defaultTabLevelColors.hoverColor,
                    activeBgColor: parsed.tabBottomActiveBgColor || defaultTabLevelColors.activeBgColor,
                    inactiveBgColor: parsed.tabBottomInactiveBgColor || defaultTabLevelColors.inactiveBgColor,
                    hoverBgColor: parsed.tabBottomHoverBgColor || defaultTabLevelColors.hoverBgColor
                };
            }

            if (parsed.tabVeryBottom && typeof parsed.tabVeryBottom === 'object') {
                tabVeryBottom = { ...defaultTabLevelColors, ...parsed.tabVeryBottom };
            } else if (parsed.tabVeryBottomActiveColor !== undefined) {
                tabVeryBottom = {
                    activeColor: parsed.tabVeryBottomActiveColor || defaultTabLevelColors.activeColor,
                    inactiveColor: parsed.tabVeryBottomInactiveColor || defaultTabLevelColors.inactiveColor,
                    hoverColor: parsed.tabVeryBottomHoverColor || defaultTabLevelColors.hoverColor,
                    activeBgColor: parsed.tabVeryBottomActiveBgColor || defaultTabLevelColors.activeBgColor,
                    inactiveBgColor: parsed.tabVeryBottomInactiveBgColor || defaultTabLevelColors.inactiveBgColor,
                    hoverBgColor: parsed.tabVeryBottomHoverBgColor || defaultTabLevelColors.hoverBgColor
                };
            }

            // Tab-Style Migration
            const tabStyle = parsed.tabStyle || parsed.tabTopStyle || defaultConfig.tabStyle;
            const tabBorderColor = parsed.tabBorderColor || parsed.tabTopBorderColor || defaultConfig.tabBorderColor;

            return {
                ...defaultConfig,
                ...parsed,
                tabStyle,
                tabBorderColor,
                tabTop,
                tabMiddle,
                tabBottom,
                tabVeryBottom
            };
        } catch (error) {
            console.error('Fehler beim Parsen der Config:', error);
            return this.getDefaultConfig();
        }
    }

    // Gibt die Standard-Konfiguration zurück
    getDefaultConfig = (): SimpleDesignConfig => {
        return JSON.parse(JSON.stringify(defaultDesignConfig));
    }

    // Aktualisiert eine einzelne Config-Eigenschaft
    updateConfig = (key: string, value: any) => {
        this.setState(prevState => {
            let newConfig = {
                ...prevState.config,
                [key]: value
            };

            // Wenn Website-Hintergrund geändert wird, Accordion-Inhalt synchronisieren
            if (key === 'websiteBackgroundColor') {
                newConfig.accordionContentBgColor = value;
            }

            // Wenn Header-Farben geändert werden UND accordionMatchHeader aktiv ist
            if (newConfig.accordionMatchHeader) {
                if (key === 'headerBgColor' || key === 'headerGradientColor' ||
                    key === 'headerGradientEnabled' || key === 'headerGradientAngle') {
                    newConfig = syncAccordionToHeader(newConfig);
                }
            }

            return { config: newConfig };
        }, this.updateLiveCSS);
    }

    // Aktualisiert eine Tab-Ebenen-Farbe
    updateTabLevelColor = (level: TabLevelName, field: keyof TabLevelColors, value: string) => {
        const newConfig = { ...this.state.config };
        newConfig[level] = {
            ...newConfig[level],
            [field]: value
        };
        this.setState({
            config: newConfig,
            workingConfig: newConfig,
            selectedVersion: null,
            selectedTemplateId: null
        });
    }

    // Kopiert Farben einer Ebene auf alle anderen Ebenen
    copyTabColorsToAllLevels = (sourceLevel: TabLevelName) => {
        const sourceColors = { ...this.state.config[sourceLevel] };
        const newConfig = {
            ...this.state.config,
            tabTop: { ...sourceColors },
            tabMiddle: { ...sourceColors },
            tabBottom: { ...sourceColors },
            tabVeryBottom: { ...sourceColors }
        };
        this.setState({
            config: newConfig,
            workingConfig: newConfig,
            selectedVersion: null,
            selectedTemplateId: null,
            successMessage: `Farben von "${tabLevelLabels[sourceLevel]}" auf alle Ebenen übertragen.`
        });
        setTimeout(() => this.setState({ successMessage: null }), 3000);
    }

    // Deep Copy einer Config
    cloneConfig = (config: SimpleDesignConfig): SimpleDesignConfig => {
        return JSON.parse(JSON.stringify(config));
    }

    // ============================================================
    // DATA LOADING
    // ============================================================

    loadAvailableVersions = async () => {
        this.setState({ isLoadingVersions: true });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/Design/versions?kunde=${this.props.kunde}&gemeinde=${this.props.gemeinde}&jahr=${this.props.jahr}&ansicht=1`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Versionen');
            }

            const versions = await response.json();
            const liveVersion = versions.find((v: any) => v.istLive === true);

            this.setState({
                availableVersions: versions,
                currentLiveVersion: liveVersion?.version || null,
                isLoadingVersions: false
            });

        } catch (error) {
            console.error('Fehler beim Laden der Versionen:', error);
            this.setState({
                isLoadingVersions: false,
                availableVersions: []
            });
        }
    }

    // Lädt die existierende Live-Konfiguration, oder fällt auf Standard zurück
    loadExistingConfig = async () => {
        this.setState({ isLoading: true });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/Design/config?kunde=${this.props.kunde}&gemeinde=${this.props.gemeinde}&jahr=${this.props.jahr}&ansicht=1`;

            const response = await fetch(url);

            if (response.ok) {
                const data = await response.json();

                if (data && data.konfiguration) {
                    const loadedConfig = this.parseConfig(data.konfiguration);

                    this.setState({
                        config: loadedConfig,
                        workingConfig: this.cloneConfig(loadedConfig),
                        isLoading: false
                    }, () => {
                        this.updateLiveCSS();
                    });
                    return;
                }
            }

            // Fallback: Keine DB-Config gefunden → verwende originalConfig
            this.applyDefaultConfig();

        } catch (error) {
            console.error('Fehler beim Laden der Config:', error);
            // auch im Fehlerfall auf originalConfig zurücksetzen
            this.applyDefaultConfig();
        }
    }

    // Hilfsmethode für Fallback auf Standard-Design
    applyDefaultConfig = () => {
        this.setState({
            config: JSON.parse(JSON.stringify(this.state.originalConfig)),
            workingConfig: JSON.parse(JSON.stringify(this.state.originalConfig)),
            isDraftLoaded: false,
            isLoading: false,
            selectedVersion: null
        }, () => {
            this.updateLiveCSS();
        });
    }

    loadDesignHistory = async () => {
        this.setState({ isLoadingHistory: true });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/design/history?kunde=${this.props.kunde}&gemeinde=${this.props.gemeinde}&jahr=${this.props.jahr}&ansicht=${this.props.verwaltungsansicht ? 2 : 1}`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            this.setState({
                designHistory: data || [],
                isLoadingHistory: false
            });
        } catch (error) {
            this.setState({
                designHistory: [],
                isLoadingHistory: false
            });
        }
    }

    loadDesignTemplates = async () => {
        this.setState({ isLoadingTemplates: true });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/design/templates`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            this.setState({
                designTemplates: data || [],
                isLoadingTemplates: false
            });
        } catch (error) {
            this.setState({
                designTemplates: [],
                isLoadingTemplates: false
            });
        }
    }

    // Aktualisiert das Live-CSS im Preview-Bereich
    updateLiveCSS = () => {
        const { config } = this.state;
        const css = this.generateCSS(config);

        // Altes Style-Element entfernen
        const oldStyle = document.getElementById(this.styleElementId);
        if (oldStyle) oldStyle.remove();

        // Neues Style-Element erstellen
        const style = document.createElement('style');
        style.id = this.styleElementId;
        style.setAttribute('data-source', 'design-configurator');
        style.appendChild(document.createTextNode(css));

        // An <body> anhängen für höhere Cascade-Priorität
        document.body.appendChild(style);
    }

    removePreviewStyles = () => {
        const style = document.getElementById(this.styleElementId);
        if (style) style.remove();
    }

    // ============================================================
    // CSS GENERATION - 100% SYNCHRON MIT BACKEND
    // ============================================================
    // diese Methode muss exakt dem Backend DesignController.GenerateCSS() entsprechen
    // Einziger Unterschied: Frontend mit Scope '.dc-preview-content' (isolierte Vorschau)
    // Backend ohne Scope (globales CSS für Live-Seite)
    // ============================================================

    /**
     * Generiert das vollständige CSS aus der Design-Konfiguration.
     * SYNCHRON mit Backend GenerateCSS() in DesignController.cs
     */
    generateCSS = (config: SimpleDesignConfig): string => {
        const timestamp = new Date().toISOString();
        const scope = '.dc-preview-content';
        const s = `${scope}[class]`;

        const headingFontWeight = config.headingBold ? 'bold' : 'normal';

        // Tab-Style CSS generieren
        const tabStyleCSS = getTabStyleCSS(config.tabStyle, scope, config.tabBorderColor);

        const levelColors = {
            tabTop: config.tabTop,
            tabMiddle: config.tabMiddle,
            tabBottom: config.tabBottom,
            tabVeryBottom: config.tabVeryBottom
        };

        const tabColorsCSS = generateTabColorsCSS(
            scope,
            levelColors,
            config.websiteBackgroundColor,
            config.tabBorderColor
        );

        // Header CSS generieren
        const headerCSS = this.generateHeaderCSS(config, scope);

        // Dokumentenbaum-Hintergründe nur überschreiben, wenn Headerfarbe vom Standard abweicht
        const headerColorDeviates = (config.headerBgColor || '').trim().toLowerCase() !== (defaultDesignConfig.headerBgColor || '').trim().toLowerCase();
        const treeBgOverrideCSS = headerColorDeviates ? `
${s} {
    --colHintergundSelected: ${this.pickTreeBgColor(config.headerBgColor, config.headlineSecondColor, 'strong')};
}

${s} .kslplus-dokument-tree .MuiTreeItem-content.Mui-selected,
${s} .kslplus-dokument-tree .MuiTreeItem-content.Mui-focused,
${s} .kslplus-dokument-tree .MuiTreeItem-content.Mui-selected.Mui-focused {
    background-color: ${this.pickTreeBgColor(config.headerBgColor, config.headlineSecondColor, 'subtle')} !important;
}
` : '';

        return `
/* ================================================================
   KSLplus Custom Design - LIVE PREVIEW
   Generated: ${timestamp}
   Scope: ${scope}
   ================================================================ */

/* ================================================================
   CSS VARIABLEN - SYNCHRON mit Backend
   ================================================================ */
${s} {
    --colHintergrund: ${config.websiteBackgroundColor};
    --colSchriftHeadlineMain: ${config.headlineMainColor};
    --colSchriftHeadlineScond: ${config.headlineSecondColor};
    --colSchriftAccordion: ${config.accordionTextColor};
    --colSchriftAccordionContent: ${config.accordionContentTextColor};
    --colSchriftAccordionContentHintergrund: ${config.accordionContentBgColor};
    --colHintergrundAccordion: ${config.accordionBgColor};
    --colHintergrundAccordionAktiv: ${config.accordionActiveBgColor};
    --colTabBorder: ${config.tabBorderColor};
    --colTabActiveBolder: ${config.tabBorderColor};
    --colButtonBackground: ${config.buttonBgColor};
    --colButtonBackgroundHover: ${config.buttonHoverColor};
    --colButtonText: ${config.buttonTextColor};
}

/* ================================================================
   WEBSITE HINTERGRUND
   ================================================================ */
${s},
${s} .App,
${s} .auth-wrapper,
${s} .kslplus-layout,
${s} .kslplus-app-wrapper,
${s} .kslplus-content-wrapper,
${s} .kslplus-content-rightside,
${s} .kslplus-content {
    background-color: ${config.websiteBackgroundColor} !important;
}

/* ================================================================
   SCHRIFTEN - GLOBAL (nur innerhalb Preview)
   ================================================================ */

/* Universeller Schriftart-Reset für Preview-Inhalt */
${s},
${s} *,
${s} *::before,
${s} *::after {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* Explizite Selektoren für Elemente */
${s} .kslplus-app-container,
${s} .kslplus-content,
${s} .kslplus-layout,
${s} .kslplus-headline,
${s} .kslplus-headline-main,
${s} .kslplus-headline-second,
${s} .kslplus-beschreibung-text,
${s} .kslplus-beschreibung-content {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* Tabs - alle Ebenen */
${s} .kslplus-haushaltstab-wrapper-top,
${s} .kslplus-haushaltstab-wrapper-top *,
${s} .kslplus-haushaltstab-wrapper-middle,
${s} .kslplus-haushaltstab-wrapper-middle *,
${s} .kslplus-haushaltstab-wrapper-bottom,
${s} .kslplus-haushaltstab-wrapper-bottom *,
${s} .kslplus-haushaltstab-wrapper-very-bottom,
${s} .kslplus-haushaltstab-wrapper-very-bottom *,
${s} .nav-tabs,
${s} .nav-tabs *,
${s} .nav-link,
${s} .nav-item {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* Tabellen - Bootstrap und React-Bootstrap-Table */
${s} table,
${s} table *,
${s} .table,
${s} .table *,
${s} .table th,
${s} .table td,
${s} .table thead,
${s} .table thead *,
${s} .table tbody,
${s} .table tbody *,
${s} .table-responsive,
${s} .table-responsive *,
${s} .react-bootstrap-table,
${s} .react-bootstrap-table *,
${s} .react-bootstrap-table table,
${s} .react-bootstrap-table th,
${s} .react-bootstrap-table td,
${s} .react-bootstrap-table thead,
${s} .react-bootstrap-table tbody {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* Formulare und Inputs */
${s} input,
${s} select,
${s} textarea,
${s} button,
${s} .form-control,
${s} .btn,
${s} .dropdown-menu,
${s} .dropdown-item {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* alle Text-Elemente */
${s} p,
${s} span,
${s} div,
${s} label,
${s} a,
${s} li,
${s} td,
${s} th,
${s} strong,
${s} em,
${s} small,
${s} b,
${s} i {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* Accordion / Collapse Elemente */
${s} .accordion,
${s} .accordion *,
${s} .collapse,
${s} .collapse *,
${s} .card,
${s} .card * {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* React-Select und andere Dropdown-Komponenten */
${s} [class*="react-select"],
${s} [class*="react-select"] * {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* ================================================================
   TEXTFARBEN
   ================================================================ */
${s} .kslplus-content,
${s} .kslplus-content p,
${s} .kslplus-content span,
${s} .kslplus-content div,
${s} .kslplus-content label,
${s} .kslplus-beschreibung-content,
${s} .kslplus-beschreibung-content p,
${s} .kslplus-beschreibung-content span {
    color: ${config.textColor} !important;
}

/* ================================================================
   ÜBERSCHRIFTEN (h1-h6) - font-weight wird hier gesetzt
   ================================================================ */
${s} h1, ${s} h2, ${s} h3, ${s} h4, ${s} h5, ${s} h6,
${s} .kslplus-content h1,
${s} .kslplus-content h2,
${s} .kslplus-content h3,
${s} .kslplus-content h4,
${s} .kslplus-content h5,
${s} .kslplus-content h6,
${s} .table h1, ${s} .table h2, ${s} .table h3, 
${s} .table h4, ${s} .table h5, ${s} .table h6,
${s} .react-bootstrap-table h1, ${s} .react-bootstrap-table h2, ${s} .react-bootstrap-table h3,
${s} .react-bootstrap-table h4, ${s} .react-bootstrap-table h5, ${s} .react-bootstrap-table h6 {
    color: ${config.headingColor} !important;
    font-weight: ${headingFontWeight} !important;
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* ================================================================
   TABELLEN-HEADER (th) - NUR Schriftart, font-weight bleibt erhalten
   ================================================================ */
${s} th,
${s} thead th,
${s} .table th,
${s} .table thead th,
${s} .react-bootstrap-table th,
${s} .react-bootstrap-table thead th {
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
}

/* ================================================================
   HEADER
   ================================================================ */
${headerCSS}

${s} .kslplus-headline-main,
${s} h1.kslplus-headline-main,
${s} h2.kslplus-headline-main {
    color: ${config.headlineMainColor} !important;
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
    font-weight: ${headingFontWeight} !important; 
}

${s} .kslplus-headline-second,
${s} h2.kslplus-headline-second,
${s} h3.kslplus-headline-second {
    color: ${config.headlineSecondColor} !important;
    font-family: '${config.fontFamily}', Arial, sans-serif !important;
    font-weight: ${headingFontWeight} !important;
}

${s} .kslplus-dokument-tree .MuiTreeItem-label {
    color: ${config.headlineSecondColor} !important;
}

${s} .kslplus-dokument-expand {
    color: ${config.headlineSecondColor} !important;
}

${s} .kslplus-info-badge {
    color: ${config.headlineSecondColor} !important;
}
${treeBgOverrideCSS}

/* ================================================================
   TAB STYLES (Form/Aussehen)
   ================================================================ */
${tabStyleCSS}

/* ================================================================
   TABS - EBENENSPEZIFISCHE FARBEN
   ================================================================ */
${tabColorsCSS}

/* ================================================================
   BUTTONS
   ================================================================ */
${this.generateButtonCSS(scope)}

/* ================================================================
   ACCORDION / BEGRÜSSUNGSTEXT
   ================================================================ */
${this.generateAccordionCSS(scope)}
    `.trim();
    }

    /**
     * Generiert Header-CSS basierend auf Gradient-Einstellungen.
     * SYNCHRON mit Backend GenerateHeaderCSS() in DesignController.cs
     */
    generateHeaderCSS = (config: SimpleDesignConfig, scope: string): string => {
        const s = `${scope}[class]`;
        const headingFontWeight = config.headingBold ? 'bold' : 'normal';

        if (config.headerGradientEnabled) {
            // Eigener Farbverlauf aktiviert
            const gradientValue = `linear-gradient(${config.headerGradientAngle}deg, ${config.headerBgColor}, ${config.headerGradientColor})`;

            return `
${s} .kslplus-headline,
${s} .kslplus-headline > .row,
${s} div.kslplus-headline {
    background: ${gradientValue} !important;
}

${s} .kslplus-headline-wrapper-left,
${s} .kslplus-headline-wrapper-middle,
${s} .kslplus-headline-wrapper-right {
    background: transparent !important;
    min-height: ${config.headerHeight}px !important;
    height: ${config.headerHeight}px !important;
}

${s} .kslplus-headline-small {
    background: ${gradientValue} !important;
}`;
        } else if (config.headerGradientToWhite) {
            // Klassischer Gradient (Links/Rechts: Farbe → Weiß → Farbe)
            return `
${s} .kslplus-headline,
${s} .kslplus-headline > .row,
${s} div.kslplus-headline {
    background: transparent !important;
}

${s} .kslplus-headline-wrapper-left {
    background: linear-gradient(to right, ${config.headerBgColor}, #FFFFFF) !important;
    min-height: ${config.headerHeight}px !important;
    height: ${config.headerHeight}px !important;
}

${s} .kslplus-headline-wrapper-middle {
    background: #FFFFFF !important;
    min-height: ${config.headerHeight}px !important;
    height: ${config.headerHeight}px !important;
}

${s} .kslplus-headline-wrapper-right {
    background: linear-gradient(to left, ${config.headerBgColor}, #FFFFFF) !important;
    min-height: ${config.headerHeight}px !important;
    height: ${config.headerHeight}px !important;
}

${s} .kslplus-headline-small {
    background: ${config.headerBgColor} !important;
}`;
        } else {
            // Einfarbiger Header
            return `
${s} .kslplus-headline,
${s} .kslplus-headline > .row,
${s} div.kslplus-headline {
    background: transparent !important;
}

${s} .kslplus-headline-wrapper-left,
${s} .kslplus-headline-wrapper-middle,
${s} .kslplus-headline-wrapper-right {
    background: ${config.headerBgColor} !important;
    min-height: ${config.headerHeight}px !important;
    height: ${config.headerHeight}px !important;
}

${s} .kslplus-headline-small {
    background: ${config.headerBgColor} !important;
}`;
        }
    }

    /**
     * Generiert Accordion/Begrüßungstext-CSS.
     * SYNCHRON mit Backend GenerateAccordionCSS() in DesignController.cs
     */
    generateAccordionCSS = (scope: string): string => {
        const { config } = this.state;
        const s = `${scope}[class]`;

        // Bestimme Hintergrundfarben basierend auf accordionMatchHeader
        let bgStyle: string;
        let activeBgStyle: string;

        if (config.accordionMatchHeader) {
            // Header-Farben übernehmen
            if (config.headerGradientEnabled) {
                const gradient = `linear-gradient(${config.headerGradientAngle}deg, ${config.headerBgColor}, ${config.headerGradientColor})`;
                bgStyle = gradient;
                activeBgStyle = gradient;
            } else {
                bgStyle = config.headerBgColor;
                activeBgStyle = config.headerBgColor;
            }
        } else {
            // Manuelle Farben
            bgStyle = config.accordionBgColor;
            activeBgStyle = config.accordionActiveBgColor;
        }

        // Inhalt-Hintergrund = Website-Hintergrund
        const contentBgColor = config.websiteBackgroundColor;

        return `
/* ================================================================
   BEGRÜSSUNGSTEXT / ACCORDION
   ================================================================ */

/* Dropdown-Balken (geschlossen) */
${s} .kslplus-beschreibung-toggle {
    background: ${bgStyle} !important;
    color: ${config.accordionTextColor} !important;
}

/* Dropdown-Balken (geöffnet/aktiv) */
${s} .kslplus-beschreibung-toggle.active {
    background: ${activeBgStyle} !important;
}

/* Text im Balken */
${s} .kslplus-beschreibung-text {
    color: ${config.accordionTextColor} !important;
}

/* Inhalt (ausgeklappt) - Hintergrund = Website-Hintergrund */
${s} .kslplus-beschreibung-content {
    background-color: ${contentBgColor} !important;
}

/* Icon im Balken */
${s} .kslplus-beschreibung-icon {
    stroke: ${config.accordionTextColor} !important;
}
`;
    }

    /**
     * Generiert Button-CSS.
     * SYNCHRON mit Backend GenerateButtonCSS() in DesignController.cs
     */
    generateButtonCSS = (scope: string): string => {
        const { config } = this.state;
        const s = `${scope}[class]`;

        // Berechne dunklere Border-Farbe (10% dunkler als Hover)
        const borderColor = this.darkenColor(config.buttonHoverColor, 5);

        return `
/* Button-Grundstyles (.btn-primary, .btn-secondary) */
${s} .btn-primary,
${s} .btn-secondary {
    background-color: ${config.buttonBgColor} !important;
    border-color: ${borderColor} !important;
    color: ${config.buttonTextColor} !important;
}

/* Button Hover */
${s} .btn-primary:hover,
${s} .btn-secondary:hover {
    background-color: ${config.buttonHoverColor} !important;
    border-color: ${borderColor} !important;
    color: ${config.buttonTextColor} !important;
}

/* Button Focus */
${s} .btn-primary:focus,
${s} .btn-secondary:focus {
    background-color: ${config.buttonBgColor} !important;
    border-color: ${borderColor} !important;
    color: ${config.buttonTextColor} !important;
    box-shadow: 0 0 0 0.2rem ${config.buttonBgColor}40 !important;
}

/* Button Active */
${s} .btn-primary:active,
${s} .btn-secondary:active {
    background-color: ${config.buttonHoverColor} !important;
    border-color: ${borderColor} !important;
    color: ${config.buttonTextColor} !important;
}

/* Button Disabled */
${s} .btn-primary:disabled,
${s} .btn-secondary:disabled {
    background-color: ${config.buttonBgColor} !important;
    border-color: ${borderColor} !important;
    color: ${config.buttonTextColor} !important;
    opacity: 0.65;
}

/* Icons in Buttons (SVG) */
${s} .btn-primary svg,
${s} .btn-secondary svg {
    color: ${config.buttonTextColor} !important;
    fill: currentColor;
}
`;
    }

    // ============================================================
    // COLOR UTILITIES
    // ============================================================

    darkenColor = (hex: string | undefined, percent: number): string => {
        if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return '#000000';
        hex = hex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const factor = 1 - (percent / 100);
        const newR = Math.round(r * factor);
        const newG = Math.round(g * factor);
        const newB = Math.round(b * factor);
        return '#' + [newR, newG, newB].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
    }

    lightenColor = (hex: string | undefined, percent: number): string => {
        if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return 'rgba(0,0,0,0.05)';
        hex = hex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const factor = percent / 100;
        const newR = Math.round(r + (255 - r) * factor);
        const newG = Math.round(g + (255 - g) * factor);
        const newB = Math.round(b + (255 - b) * factor);
        return '#' + [newR, newG, newB].map(x => Math.min(255, x).toString(16).padStart(2, '0')).join('');
    }

    // relative Luminanz nach WCAG (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance)
    relativeLuminance = (hex: string | undefined): number => {
        if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return 0;
        const h = hex.replace('#', '');
        if (h.length !== 6) return 0;
        const r = parseInt(h.substring(0, 2), 16) / 255;
        const g = parseInt(h.substring(2, 4), 16) / 255;
        const b = parseInt(h.substring(4, 6), 16) / 255;
        const lin = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    }

    // Kontrastverhältnis nach WCAG (1:1 bis 21:1)
    contrastRatio = (hex1: string | undefined, hex2: string | undefined): number => {
        const l1 = this.relativeLuminance(hex1);
        const l2 = this.relativeLuminance(hex2);
        return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    }

    // wählt den Tree-Hintergrund, der vom Header abgesetzt UND zum Text gut lesbar ist
    // erzeugt zwei Kandidaten (heller / dunkler als Header) und nimmt den mit höherem Kontrast zum Text
    // mode: 'strong' = Türkis-Ersatz (Hover), 'subtle' = Grau-Ersatz (Selected/Focused)
    pickTreeBgColor = (headerHex: string | undefined, textHex: string | undefined, mode: 'strong' | 'subtle'): string => {
        const lightP = mode === 'strong' ? 65 : 92;
        const darkP = mode === 'strong' ? 35 : 65;
        const lightCand = this.lightenColor(headerHex, lightP);
        const darkCand = this.darkenColor(headerHex, darkP);
        return this.contrastRatio(lightCand, textHex) >= this.contrastRatio(darkCand, textHex)
            ? lightCand
            : darkCand;
    }

    // ============================================================
    // EVENT HANDLERS
    // ============================================================

    handleColorChange = (field: keyof SimpleDesignConfig, value: string) => {
        const newConfig = { ...this.state.config, [field]: value };
        this.setState({
            config: newConfig,
            workingConfig: newConfig,
            selectedVersion: null,
            selectedTemplateId: null
        });
    }

    handleNumberChange = (field: keyof SimpleDesignConfig, value: number) => {
        const newConfig = { ...this.state.config, [field]: value };
        this.setState({
            config: newConfig,
            workingConfig: newConfig,
            selectedVersion: null,
            selectedTemplateId: null
        });
    }

    handleBooleanChange = (field: keyof SimpleDesignConfig, value: boolean) => {
        let newConfig = { ...this.state.config, [field]: value };

        // Gegenseitige Ausschließung der Gradient-Optionen
        if (field === 'headerGradientEnabled' && value === true) {
            newConfig.headerGradientToWhite = false;
        } else if (field === 'headerGradientToWhite' && value === true) {
            newConfig.headerGradientEnabled = false;
        }

        this.setState({
            config: newConfig,
            workingConfig: newConfig,
            selectedVersion: null,
            selectedTemplateId: null
        });
    }

    handleStringChange = (field: keyof SimpleDesignConfig, value: string) => {
        const newConfig = { ...this.state.config, [field]: value };
        this.setState({
            config: newConfig,
            workingConfig: newConfig,
            selectedVersion: null,
            selectedTemplateId: null
        });
    }

    // Inline Confirmation
    requestConfirmation = (action: State['pendingAction'], data?: any) => {
        this.setState({ pendingAction: action, pendingActionData: data });
    }

    cancelPendingAction = () => {
        this.setState({ pendingAction: null, pendingActionData: null });
    }

    confirmPendingAction = async () => {
        const { pendingAction, pendingActionData } = this.state;
        this.setState({ pendingAction: null, pendingActionData: null });

        switch (pendingAction) {
            case 'reset':
                this.executeReset();
                break;
            case 'save':
                await this.executeSave();
                break;
            case 'saveAndActivate':
                await this.executeSaveAndActivate();
                break;
            case 'activateVersion':
                await this.executeActivateVersion(pendingActionData);
                break;
            case 'applyTemplate':
                await this.executeApplyTemplate(pendingActionData.id, pendingActionData.name);
                break;
            case 'deleteVersion':
                await this.executeDeleteVersion(pendingActionData);
                break;
            case 'transferDesign':
                await this.executeTransferDesign();
                break;
        }
    }

    handleReset = () => {
        this.requestConfirmation('reset');
    }

    executeReset = () => {
        console.log('[DesignConfigurator] Reset auf Original-Konfiguration');
        console.log('[DesignConfigurator] Hat Legacy-CSS:', this.state.hasLegacyCSS);

        this.setState({
            config: JSON.parse(JSON.stringify(this.state.originalConfig)),
            workingConfig: JSON.parse(JSON.stringify(this.state.originalConfig)),
            selectedVersion: null,
            selectedTemplateId: null,
            isDraftLoaded: false
        }, () => {
            this.updateLiveCSS();

            // Erfolgs-Meldung anzeigen
            const message = this.state.hasLegacyCSS
                ? 'Zurückgesetzt auf kundenspezifisches Design'
                : 'Zurückgesetzt auf Standard-Design';

            this.setState({ successMessage: message });

            setTimeout(() => {
                this.setState({ successMessage: null });
            }, 3000);
        });
    }

    handleClose = () => {
        this.removePreviewStyles();
        this.props.onClose();
    }

    handleSave = () => {
        this.requestConfirmation('save');
    }

    executeSave = async () => {
        this.setState({ isSaving: true, error: null, successMessage: null });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const designClient = new KSLPlusAPI.Design_Client(api);
            const configModel = this.createConfigModel();

            const request = new KSLPlusAPI.SaveDesignConfigRequest();
            request.kunde = this.props.kunde;
            request.gemeinde = this.props.gemeinde;
            request.jahr = this.props.jahr;
            request.ansicht = 1;
            request.konfiguration = configModel;

            const response = await designClient.configPost(undefined, request);

            if (!response || typeof response.version !== 'number' || response.version < 1) {
                throw new Error('Ungültige Version in Response');
            }

            const savedConfig = this.cloneConfig(this.state.config);

            this.setState({
                isSaving: false,
                successMessage: `Version ${response.version} wurde gespeichert (noch nicht live).`,
                workingConfig: savedConfig,
                originalConfig: savedConfig
            });

            await this.loadAvailableVersions();

            setTimeout(() => this.setState({ successMessage: null }), 5000);

        } catch (error) {
            this.setState({
                error: 'Fehler beim Speichern: ' + (error as Error).message,
                isSaving: false
            });
        }
    }

    handleSaveAndActivate = () => {
        this.requestConfirmation('saveAndActivate');
    }

    executeSaveAndActivate = async () => {
        this.setState({ isSaving: true, error: null, successMessage: null });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const designClient = new KSLPlusAPI.Design_Client(api);
            const configModel = this.createConfigModel();

            const saveRequest = new KSLPlusAPI.SaveDesignConfigRequest();
            saveRequest.kunde = this.props.kunde;
            saveRequest.gemeinde = this.props.gemeinde;
            saveRequest.jahr = this.props.jahr;
            saveRequest.ansicht = 1;
            saveRequest.konfiguration = configModel;

            const response = await designClient.configPost(undefined, saveRequest);

            if (!response || typeof response.version !== 'number' || response.version < 1) {
                throw new Error('Ungültige Version in Response');
            }

            const savedVersion = response.version;

            const activateRequest = new KSLPlusAPI.ActivateDesignRequest();
            activateRequest.kunde = this.props.kunde;
            activateRequest.gemeinde = this.props.gemeinde;
            activateRequest.jahr = this.props.jahr;
            activateRequest.ansicht = 1;
            activateRequest.version = savedVersion;

            await designClient.activate(undefined, activateRequest);

            const savedConfig = this.cloneConfig(this.state.config);

            this.setState({
                isSaving: false,
                successMessage: 'Design wurde gespeichert und live geschaltet!',
                currentLiveVersion: savedVersion,
                workingConfig: savedConfig,
                originalConfig: savedConfig,
                activeTab: 'vorlagen',
                selectedVersion: savedVersion,
                selectedTemplateId: null
            });

            if (this.props.onActivate) {
                await this.props.onActivate();
            }
            await this.loadAvailableVersions();
            setTimeout(() => this.setState({ successMessage: null }), 3000);

        } catch (error) {
            const err = error as any;
            let errorMessage = 'Fehler beim Speichern und Aktivieren: ';

            if (err.status === 401) {
                errorMessage = 'Sie sind nicht berechtigt, Designs zu speichern.';
            } else if (err.status === 404) {
                errorMessage = 'Design-API nicht gefunden.';
            } else {
                errorMessage += err.message || 'Unbekannter Fehler';
            }

            this.setState({
                error: errorMessage,
                isSaving: false
            });
        }
    }

    createConfigModel = (): KSLPlusAPI.DesignConfigModel => {
        return this.createConfigModelFrom(this.state.config);
    }

    // ============================================================
    // VERSION HANDLERS
    // ============================================================

    handleVersionPreview = async (version: number) => {
        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/Design/config/preview?kunde=${this.props.kunde}&gemeinde=${this.props.gemeinde}&jahr=${this.props.jahr}&ansicht=1&version=${version}`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data && data.konfiguration) {
                const loadedConfig = this.parseConfig(data.konfiguration);

                this.setState({
                    config: loadedConfig,
                    selectedVersion: version,
                    selectedTemplateId: null,
                    previewConfig: loadedConfig
                }, () => {
                    this.updateLiveCSS();
                });
            }
        } catch (error) {
            console.error('Fehler beim Laden der Version:', error);
            this.setState({ error: 'Fehler beim Laden der Version: ' + (error as Error).message });
        }
    }

    handleActivateVersion = (version: number) => {
        this.requestConfirmation('activateVersion', version);
    }

    executeActivateVersion = async (version: number) => {
        this.setState({ isSaving: true, error: null });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const designClient = new KSLPlusAPI.Design_Client(api);

            for (const ansicht of [1, 2]) {
                const request = new KSLPlusAPI.ActivateDesignRequest();
                request.kunde = this.props.kunde;
                request.gemeinde = this.props.gemeinde;
                request.jahr = this.props.jahr;
                request.ansicht = ansicht;
                request.version = version;

                await designClient.activate(undefined, request);
            }

            this.setState({
                successMessage: `Version ${version} wurde live geschaltet!`,
                isSaving: false,
                currentLiveVersion: version,
                activeTab: 'vorlagen'
            });

            if (this.props.onActivate) {
                await this.props.onActivate();
            }
            await this.loadAvailableVersions();
            setTimeout(() => this.setState({ successMessage: null }), 3000);

        } catch (error) {
            this.setState({
                error: 'Fehler beim Aktivieren: ' + (error as Error).message,
                isSaving: false
            });
        }
    }

    handleDeleteVersion = (version: number, isLive: boolean = false) => {
        this.requestConfirmation('deleteVersion', { version, isLive });
    }

    /**
 * Entfernt alle Designer-bezogenen Style-Elemente aus dem DOM.
 * Wird vor dem Reload nach Löschen der Live-Version aufgerufen.
 */
    removeAllDesignerStyles = (): void => {
        // Preview-Styles entfernen
        const previewStyle = document.getElementById(this.styleElementId);
        if (previewStyle) {
            previewStyle.remove();
        }

        // DB-Design-Styles entfernen
        const dbStyle = document.getElementById('kslplus-custom-design');
        if (dbStyle) {
            dbStyle.remove();
        }

        // Legacy-CSS entfernen (falls vorhanden)
        const legacyStyle = document.getElementById('kslplus-legacy-css');
        if (legacyStyle) {
            legacyStyle.remove();
        }
    }

    executeDeleteVersion = async (data: { version: number; isLive: boolean }) => {
        const { version, isLive } = data;
        this.setState({ isSaving: true, error: null });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const designClient = new KSLPlusAPI.Design_Client(api);

            await designClient.versionsDelete(
                version,
                this.props.kunde,
                this.props.gemeinde,
                this.props.jahr,
                1
            );

            if (isLive) {
                this.removeAllDesignerStyles();

                this.setState({
                    successMessage: 'Live-Version wurde gelöscht. Design wurde zurückgesetzt.',
                    isSaving: false,
                    currentLiveVersion: null,
                    activeTab: 'vorlagen'
                });

                if (this.props.onActivate) {
                    await this.props.onActivate();
                }
                this.updateLiveCSS();
                await this.loadAvailableVersions();
                setTimeout(() => this.setState({ successMessage: null }), 3000);
            } else {
                this.setState({
                    successMessage: `Version ${version} wurde erfolgreich gelöscht.`,
                    isSaving: false,
                    selectedVersion: this.state.selectedVersion === version ? null : this.state.selectedVersion
                });

                await this.loadAvailableVersions();
                setTimeout(() => this.setState({ successMessage: null }), 3000);
            }

        } catch (error) {
            this.setState({
                error: 'Fehler beim Löschen: ' + (error as Error).message,
                isSaving: false
            });
        }
    }

    checkLegacyCSSExists = async (): Promise<boolean> => {
        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const client = new KSLPlusAPI.DigitalHoushold_Client(api);

            const texte = await client.texte(
                this.props.kunde,
                this.props.gemeinde,
                this.props.jahr,
                3,
                ""
            );

            return !!(texte && texte.length > 0 && texte[0].text && texte[0].text.length > 0);

        } catch (error) {
            return false;
        }
    }

    // ============================================================
    // TEMPLATE HANDLERS
    // ============================================================

    handleApplyTemplate = (templateId: number, templateName: string) => {
        this.requestConfirmation('applyTemplate', { id: templateId, name: templateName });
    }

    executeApplyTemplate = async (templateId: number, templateName: string) => {
        this.setState({ isSaving: true, error: null });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/design/templates/apply`;

            const request = {
                templateId: templateId,
                kunde: this.props.kunde,
                gemeinde: this.props.gemeinde,
                jahr: this.props.jahr,
                ansicht: 1
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(request)
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            this.setState({
                successMessage: `Template "${templateName}" angewendet!`,
                isSaving: false,
                selectedTemplateId: null,
                selectedVersion: null
            });

            await this.loadExistingConfig();
            await this.loadAvailableVersions();

            setTimeout(() => this.setState({ successMessage: null }), 3000);

        } catch (error) {
            this.setState({
                error: 'Fehler beim Anwenden: ' + (error as Error).message,
                isSaving: false
            });
        }
    }

    handleTemplatePreview = (template: DesignTemplateResponse) => {
        let templateConfig: SimpleDesignConfig = this.getDefaultConfig();
        try {
            if (template.konfigurationJson) {
                templateConfig = this.parseConfig(template.konfigurationJson);
            }
        } catch (e) {
            console.warn('Fehler beim Parsen der Template-Konfiguration:', e);
        }

        this.setState({
            config: templateConfig,
            selectedTemplateId: template.id,
            selectedVersion: null
        });
    }

    // ============================================================
    // MULTI-GEMEINDE: DESIGN AUF ANDERE GEMEINDEN ÜBERTRAGEN
    // ============================================================

    // lädt die weiteren Gemeinden des Kunden (ohne die aktuelle Gemeinde)
    loadCustomerGemeinden = async () => {
        this.setState({ isLoadingGemeinden: true });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/Design/gemeinden?kunde=${this.props.kunde}&gemeinde=${this.props.gemeinde}`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            this.setState({
                customerGemeinden: data || [],
                isLoadingGemeinden: false
            });
        } catch (error) {
            this.setState({
                customerGemeinden: [],
                isLoadingGemeinden: false
            });
        }
    }

    // wählt eine einzelne Ziel-Gemeinde an oder ab
    toggleTransferGemeinde = (gemeindeId: number) => {
        this.setState(prevState => {
            const selected = prevState.selectedTransferGemeinden.includes(gemeindeId)
                ? prevState.selectedTransferGemeinden.filter(id => id !== gemeindeId)
                : [...prevState.selectedTransferGemeinden, gemeindeId];
            return { selectedTransferGemeinden: selected };
        });
    }

    // wählt alle Gemeinden an bzw. (wenn bereits alle angewählt sind) wieder ab
    toggleAllTransferGemeinden = () => {
        this.setState(prevState => {
            const allIds = prevState.customerGemeinden.map(g => g.gemeinde);
            const allSelected = allIds.length > 0 && allIds.every(id => prevState.selectedTransferGemeinden.includes(id));
            return { selectedTransferGemeinden: allSelected ? [] : allIds };
        });
    }

    handleTransferDesign = () => {
        this.requestConfirmation('transferDesign');
    }

    // überträgt das aktuelle Live-Design auf die ausgewählten Gemeinden und schaltet es dort live
    executeTransferDesign = async () => {
        this.setState({ isTransferring: true, error: null, successMessage: null });

        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const url = `${api}/api/Design/transfer`;

            const request = {
                kunde: this.props.kunde,
                gemeinde: this.props.gemeinde,
                jahr: this.props.jahr,
                zielGemeinden: this.state.selectedTransferGemeinden
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(request)
            });

            if (!response.ok) {
                let message = `HTTP ${response.status}`;
                try {
                    const errData = await response.json();
                    if (errData && errData.error) message = errData.error;
                } catch {
                    // Antwort ohne JSON-Body
                }
                throw new Error(message);
            }

            const result = await response.json();
            const anzahl = (result && typeof result.uebertragen === 'number')
                ? result.uebertragen
                : this.state.selectedTransferGemeinden.length;

            this.setState({
                isTransferring: false,
                selectedTransferGemeinden: [],
                transferDropdownOpen: false,
                successMessage: `Design auf ${this.gemeindeAnzahlWort(anzahl)} ${anzahl === 1 ? 'Gemeinde' : 'Gemeinden'} übertragen und live geschaltet.`
            });

            await this.loadCustomerGemeinden();
            setTimeout(() => this.setState({ successMessage: null }), 5000);

        } catch (error) {
            this.setState({
                error: 'Fehler beim Übertragen: ' + (error as Error).message,
                isTransferring: false
            });
        }
    }

    // ============================================================
    // SUPERADMIN: KUNDENÜBERGREIFENDE DESIGN-ÜBERTRAGUNG
    // ============================================================
    // Der eigentliche Zuordnungsdialog liegt in DesignConfiguratorSA.tsx;
    // hier ist nur der Rollencheck und der Öffnen-Button.

    // Erlaubnis/Sichtbarkeit des kundenübergreifenden Übertragungs-Tools (Button + Dialog).
    // !!!!TEST-PHASE: Admin ist zugelassen. VOR DEM COMMIT die Admin-Zeile auskommentieren!!!!,
    // sodass ausschließlich Superadmins Zugriff haben.
    canUseExternTransfer = (): boolean => {
        const roles = this.props.userRoles || [];
        if (roles.includes('SuperAdmin')) return true;
        //if (roles.includes('Admin')) return true;   // TEST: vor dem Commit auskommentieren
        return false;
    }

    // ============================================================
    // RENDER HELPERS
    // ============================================================

    renderNavButton = (id: string, icon: React.ReactNode, label: string) => {
        const { activeTab } = this.state;
        const isActive = activeTab === id;
        return (
            <button
                onClick={() => this.setState({ activeTab: id })}
                className={`dc-nav-button ${isActive ? 'dc-nav-button-active' : ''}`}
                type="button"
            >
                <div className="dc-nav-button-content">
                    <span className={`dc-nav-icon ${isActive ? 'dc-nav-icon-active' : ''}`}>{icon}</span>
                    <span>{label}</span>
                </div>
                {isActive && <FiChevronRight className="dc-nav-chevron" />}
            </button>
        );
    }

    renderColorInput = (label: string, value: string, field: keyof SimpleDesignConfig, description?: string) => {
        return (
            <div className="dc-input-card">
                <div className="dc-input-header">
                    <label className="dc-input-label">{label}</label>
                    {description && (
                        <div className="dc-tooltip-wrapper">
                            <span className="dc-tooltip-trigger"><FiInfo size={14} /></span>
                            <span className="dc-tooltip-content">{description}</span>
                        </div>
                    )}
                </div>
                <div className="dc-color-input-row">
                    <div className="dc-color-picker-mui">
                        <ColorPicker
                            value={value}
                            disableAlpha
                            disablePlainColor
                            onChange={(color: any) => {
                                if (color && color.hex) {
                                    this.handleColorChange(field, '#' + color.hex);
                                }
                            }}
                        />
                    </div>
                    <div className="dc-color-text-wrapper">
                        <span className="dc-color-hash">#</span>
                        <input
                            type="text"
                            value={value.replace('#', '')}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9a-fA-F]/g, '');
                                this.handleColorChange(field, '#' + val);
                            }}
                            className="dc-color-text-input"
                            placeholder="000000"
                            maxLength={6}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Farb-Input für Tab-Ebenen
    renderTabLevelColorInput = (label: string, level: TabLevelName, field: keyof TabLevelColors, description?: string) => {
        const value = this.state.config[level][field];
        return (
            <div className="dc-input-card dc-input-card-compact">
                <div className="dc-input-header">
                    <label className="dc-input-label">{label}</label>
                    {description && (
                        <div className="dc-tooltip-wrapper">
                            <span className="dc-tooltip-trigger"><FiInfo size={14} /></span>
                            <span className="dc-tooltip-content">{description}</span>
                        </div>
                    )}
                </div>
                <div className="dc-color-input-row">
                    <div className="dc-color-picker-mui">
                        <ColorPicker
                            value={value}
                            disableAlpha
                            disablePlainColor
                            onChange={(color: any) => {
                                if (color && color.hex) {
                                    this.updateTabLevelColor(level, field, '#' + color.hex);
                                }
                            }}
                        />
                    </div>
                    <div className="dc-color-text-wrapper">
                        <span className="dc-color-hash">#</span>
                        <input
                            type="text"
                            value={value.replace('#', '')}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9a-fA-F]/g, '');
                                this.updateTabLevelColor(level, field, '#' + val);
                            }}
                            className="dc-color-text-input"
                            placeholder="000000"
                            maxLength={6}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderRangeInput = (label: string, value: number, field: keyof SimpleDesignConfig, min: number, max: number, step: number, unit: string = 'px') => {
        const percentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
        return (
            <div className="dc-input-card">
                <div className="dc-range-header">
                    <label className="dc-input-label">{label}</label>
                    <div className="dc-range-value-wrapper">
                        <input
                            type="number"
                            value={value}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val)) this.handleNumberChange(field, val);
                            }}
                            className="dc-range-number-input"
                        />
                        <span className="dc-range-unit">{unit}</span>
                    </div>
                </div>
                <div className="dc-range-slider-wrapper">
                    <div className="dc-range-track">
                        <div className="dc-range-fill" style={{ width: `${percentage}%` }} />
                    </div>
                    <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={(e) => this.handleNumberChange(field, parseInt(e.target.value))}
                        className="dc-range-input"
                    />
                    <div className="dc-range-thumb" style={{ left: `calc(${percentage}% - 8px)` }} />
                </div>
            </div>
        );
    }

    renderSwitch = (label: string, checked: boolean, field: keyof SimpleDesignConfig, description?: string) => {
        return (
            <div className="dc-input-card">
                <div className="dc-switch-content">
                    <div className="dc-switch-text">
                        <label className="dc-input-label">{label}</label>
                        {description && <small className="dc-switch-description">{description}</small>}
                    </div>
                    <button
                        className={`dc-switch ${checked ? 'dc-switch-active' : ''}`}
                        onClick={() => this.handleBooleanChange(field, !checked)}
                        type="button"
                    >
                        <span className="dc-switch-thumb" />
                    </button>
                </div>
            </div>
        );
    }

    renderSelect = (label: string, value: string, field: keyof SimpleDesignConfig, options: { value: string; label: string }[], description?: string) => {
        return (
            <div className="dc-input-card">
                <label className="dc-input-label">{label}</label>
                <select
                    value={value}
                    onChange={(e) => this.handleStringChange(field, e.target.value)}
                    className="dc-select"
                >
                    {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                {description && <small className="dc-helper-text">{description}</small>}
            </div>
        );
    }

    // ============================================================
    // INLINE CONFIRMATION DIALOG
    // ============================================================

    // Wandelt die Anzahl ausgewählter Gemeinden in ein deutsches Zahlwort um (1-10);
    // bei größeren Werten wird die Ziffer verwendet. "eine" passt zu "Gemeinde".
    gemeindeAnzahlWort = (n: number): string => {
        const woerter = ['', 'eine', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn'];
        return woerter[n] || String(n);
    }

    renderConfirmationDialog = () => {
        const { pendingAction, pendingActionData } = this.state;

        if (!pendingAction) return null;

        let title = '';
        let message: React.ReactNode = '';

        switch (pendingAction) {
            case 'reset':
                title = 'Zurückstellen';
                const resetTarget = this.state.hasLegacyCSS
                    ? 'Ihr ursprüngliches Design'
                    : 'das KSLPlus Standard Design';
                message = (
                    <>
                        Soll Ihr erstelltes Design wirklich auf {resetTarget} zurückgestellt werden?
                        <br /><br />
                        <strong>Achtung:</strong> Nicht gespeicherte Änderungen Ihrer Arbeitsversion gehen dabei verloren.
                    </>
                );
                break;
            case 'save':
                title = 'Speichern';
                message = 'Design als neue Version speichern? (Wird noch nicht live geschaltet)';
                break;
            case 'saveAndActivate':
                title = 'Speichern & Aktivieren';
                message = 'Design speichern und sofort für alle Benutzer live schalten?';
                break;
            case 'activateVersion':
                title = 'Version aktivieren';
                message = `Version ${pendingActionData} live schalten?`;
                break;
            case 'applyTemplate':
                title = 'Template anwenden';
                message = `Template "${pendingActionData?.name}" anwenden?`;
                break;
            case 'deleteVersion':
                title = 'Version löschen';
                message = pendingActionData?.isLive
                    ? `Live-Version ${pendingActionData.version} wirklich löschen?`
                    : `Version ${pendingActionData?.version} wirklich löschen?`;
                break;
            case 'transferDesign':
                title = 'Design übertragen';
                const transferZielAnzahl = this.state.selectedTransferGemeinden.length;
                message = (
                    <>
                        Soll das aktuell live geschaltete Design auf {this.gemeindeAnzahlWort(transferZielAnzahl)} {transferZielAnzahl === 1 ? 'ausgewählte Gemeinde' : 'ausgewählte Gemeinden'} übertragen und dort live geschaltet werden?
                        <br /><br />
                        Ein bereits live geschaltetes Design der Ziel-Gemeinden wird dabei ersetzt.
                    </>
                );
                break;
        }

        return (
            <div className="dc-confirmation-overlay">
                <div className="dc-confirmation-dialog">
                    <div className="dc-confirmation-header">
                        <FiHelpCircle className="dc-confirmation-icon" />
                        <h4>{title}</h4>
                    </div>
                    <p className="dc-confirmation-message">{message}</p>
                    <div className="dc-confirmation-actions">
                        <button className="dc-dialog-btn dc-dialog-btn-cancel" onClick={this.cancelPendingAction} type="button">
                            Abbrechen
                        </button>
                        <button
                            className={`dc-dialog-btn ${pendingAction === 'deleteVersion' ? 'dc-dialog-btn-danger' : 'dc-dialog-btn-confirm'}`}
                            onClick={this.confirmPendingAction}
                            type="button"
                        >
                            Bestätigen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ============================================================
    // TAB LEVEL ACCORDION SECTION
    // ============================================================

    renderTabLevelSection = (level: TabLevelName) => {
        const { config, expandedTabLevel } = this.state;
        const colors = config[level];
        const isExpanded = expandedTabLevel === level;
        const label = tabLevelLabels[level];

        return (
            <div className="dc-accordion-item" key={level}>
                <button
                    className={`dc-accordion-header ${isExpanded ? 'active' : ''}`}
                    onClick={() => this.setState({ expandedTabLevel: isExpanded ? null : level })}
                    type="button"
                >
                    <div className="dc-accordion-header-content">
                        <span>{label}</span>
                        {/* Mini-Vorschau der Farben */}
                        <div className="dc-tab-preview-mini-inline">
                            <span
                                className="dc-color-dot"
                                style={{ backgroundColor: colors.activeBgColor }}
                                title="Aktiv Hintergrund"
                            />
                            <span
                                className="dc-color-dot"
                                style={{ backgroundColor: colors.inactiveBgColor }}
                                title="Inaktiv Hintergrund"
                            />
                        </div>
                    </div>
                    {isExpanded ? <FiChevronDown className="dc-accordion-icon" /> : <FiChevronRight className="dc-accordion-icon" />}
                </button>

                {isExpanded && (
                    <div className="dc-accordion-body">
                        {/* Mini-Vorschau */}
                        <div className="dc-tab-preview-mini" style={{ background: config.websiteBackgroundColor }}>
                            <div
                                className="dc-preview-tab-mini active"
                                style={{
                                    backgroundColor: colors.activeBgColor,
                                    color: colors.activeColor
                                }}
                            >
                                Geöffnet
                            </div>
                            <div
                                className="dc-preview-tab-mini"
                                style={{
                                    backgroundColor: colors.inactiveBgColor,
                                    color: colors.inactiveColor
                                }}
                            >
                                Geschlossen
                            </div>
                            <div
                                className="dc-preview-tab-mini"
                                style={{
                                    backgroundColor: colors.hoverBgColor,
                                    color: colors.hoverColor
                                }}
                                title="Farbe beim Überfahren mit der Maus"
                            >
                                Hover
                            </div>
                        </div>

                        <div className="dc-color-columns">
                            {/* Hintergrundfarben (links) */}
                            <div className="dc-color-column">
                                <h4 className="dc-subsection-title">Hintergrundfarben</h4>
                                {this.renderTabLevelColorInput('Geöffnet', level, 'activeBgColor')}
                                {this.renderTabLevelColorInput('Geschlossen', level, 'inactiveBgColor')}
                                {this.renderTabLevelColorInput('Hover', level, 'hoverBgColor', 'Farbe beim Überfahren mit der Maus')}
                            </div>
                            {/* Textfarben (rechts) */}
                            <div className="dc-color-column">
                                <h4 className="dc-subsection-title">Textfarben</h4>
                                {this.renderTabLevelColorInput('Geöffnet', level, 'activeColor')}
                                {this.renderTabLevelColorInput('Geschlossen', level, 'inactiveColor')}
                                {this.renderTabLevelColorInput('Hover', level, 'hoverColor', 'Farbe beim Überfahren mit der Maus')}
                            </div>
                        </div>

                        {/* Button: Farben auf alle Ebenen kopieren */}
                        <button
                            className="dc-btn-secondary dc-btn-full-width"
                            onClick={() => this.copyTabColorsToAllLevels(level)}
                            type="button"
                        >
                            <FiCopy size={14} />
                            <span>Farben auf alle Ebenen übertragen</span>
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // ============================================================
    // TAB RENDERERS
    // ============================================================

    renderHeaderTab = () => {
        const { config } = this.state;

        return (
            <div className="dc-tab-content dc-fade-in">
                <div className="dc-section-header">
                    <h3 className="dc-section-title">Header</h3>
                    <p className="dc-section-description">Passen Sie den Kopfbereich an.</p>
                </div>

                {this.renderColorInput('Hintergrundfarbe', config.headerBgColor, 'headerBgColor', 'Hauptfarbe des Headers')}

                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Farbverlauf-Optionen</h4>
                    <small className="dc-helper-text">Wählen Sie maximal eine Option</small>
                </div>

                {this.renderSwitch(
                    'Klassischer Gradient (zu Weiß)',
                    config.headerGradientToWhite,
                    'headerGradientToWhite',
                    'Links/Rechts: Farbe → Weiß → Farbe'
                )}

                {this.renderSwitch(
                    'Eigener Farbverlauf',
                    config.headerGradientEnabled,
                    'headerGradientEnabled',
                    'Durchgehender Gradient über gesamten Header'
                )}

                {config.headerGradientEnabled && (
                    <>
                        {this.renderColorInput('Verlaufsfarbe (Ende)', config.headerGradientColor, 'headerGradientColor')}
                        {this.renderRangeInput('Verlaufsrichtung', config.headerGradientAngle, 'headerGradientAngle', 0, 360, 15, '°')}
                        <div className="dc-gradient-presets">
                            {[0, 90, 180, 270, 135, 45].map(angle => (
                                <button
                                    key={angle}
                                    className={`dc-gradient-preset-btn ${config.headerGradientAngle === angle ? 'active' : ''}`}
                                    onClick={() => this.handleNumberChange('headerGradientAngle', angle)}
                                    type="button"
                                    title={`${angle}°`}
                                >
                                    {angle === 0 ? '↑' : angle === 90 ? '→' : angle === 180 ? '↓' : angle === 270 ? '←' : angle === 135 ? '↘' : '↗'}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {this.renderRangeInput('Höhe', config.headerHeight, 'headerHeight', 200, 300, 10)}

                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Überschriften</h4>
                </div>
                {this.renderSwitch('Überschriften fettgedruckt', config.headingBold, 'headingBold')}
                {this.renderColorInput('Hauptüberschrift', config.headlineMainColor, 'headlineMainColor')}
                {this.renderColorInput('Unterüberschrift', config.headlineSecondColor, 'headlineSecondColor')}
            </div>
        );
    }

    /**
 * Rendert den Tab für Begrüßungstext-Einstellungen
 * Ermöglicht Anpassung der Dropdown-Balken-Farben
 * Wird nur angezeigt wenn hasHauptText=true
 */
    renderAccordionTab = () => {
        const { config } = this.state;

        return (
            <div className="dc-tab-content dc-fade-in">
                {/* Header */}
                <div className="dc-section-header">
                    <h3 className="dc-section-title">Begrüßungstext</h3>
                    <p className="dc-section-description">
                        Farben für den ausklappbaren Begrüßungstext unter dem Header
                    </p>
                </div>

                {/* Dropdown-Balken Einstellungen */}
                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Dropdown-Balken</h4>
                </div>

                {/* Switch: Farben vom Header übernehmen */}
                <div className="dc-input-card">
                    <div className="dc-switch-content">
                        <div className="dc-switch-text">
                            <label className="dc-input-label">Farben vom Header übernehmen</label>
                            <small className="dc-switch-description">Verwendet die Header-Farben für den Balken</small>
                        </div>
                        <button
                            className={`dc-switch ${config.accordionMatchHeader ? 'dc-switch-active' : ''}`}
                            onClick={() => {
                                if (!config.accordionMatchHeader) {
                                    // Aktivieren: Farben vom Header übernehmen
                                    const newConfig = syncAccordionToHeader({
                                        ...config,
                                        accordionMatchHeader: true
                                    });
                                    this.setState({
                                        config: newConfig,
                                        workingConfig: newConfig,
                                        selectedVersion: null,
                                        selectedTemplateId: null
                                    }, this.updateLiveCSS);
                                } else {
                                    // Deaktivieren: Zu manuellen Farben wechseln
                                    const newConfig = {
                                        ...config,
                                        accordionMatchHeader: false
                                    };
                                    this.setState({
                                        config: newConfig,
                                        workingConfig: newConfig,
                                        selectedVersion: null,
                                        selectedTemplateId: null
                                    }, this.updateLiveCSS);
                                }
                            }}
                            type="button"
                        >
                            <span className="dc-switch-thumb" />
                        </button>
                    </div>
                </div>

                {/* Button: Standardfarben verwenden (nur wenn nicht Header-Match) */}
                {!config.accordionMatchHeader && (
                    <button
                        className="dc-btn-secondary dc-btn-full-width"
                        onClick={() => {
                            // Standardfarben aus defaultDesignConfig setzen
                            const newConfig = {
                                ...config,
                                accordionBgColor: defaultDesignConfig.accordionBgColor,
                                accordionActiveBgColor: defaultDesignConfig.accordionActiveBgColor,
                                accordionTextColor: defaultDesignConfig.accordionTextColor,
                                accordionMatchHeader: false
                            };
                            this.setState({
                                config: newConfig,
                                workingConfig: newConfig,
                                selectedVersion: null,
                                selectedTemplateId: null,
                                successMessage: 'Standardfarben wurden angewendet.'
                            }, this.updateLiveCSS);
                            setTimeout(() => this.setState({ successMessage: null }), 3000);
                        }}
                        type="button"
                    >
                        <FiRefreshCw size={14} />
                        <span>Auf Standardfarben zurücksetzen</span>
                    </button>

                )}

                {/* Manuelle Farbeinstellungen (nur wenn nicht "wie Header") */}
                {!config.accordionMatchHeader && (
                    <>
                        {this.renderColorInput(
                            'Hintergrundfarbe (geschlossen)',
                            config.accordionBgColor,
                            'accordionBgColor',
                            'Farbe des Balkens im geschlossenen Zustand'
                        )}

                        {this.renderColorInput(
                            'Hintergrundfarbe (geöffnet)',
                            config.accordionActiveBgColor,
                            'accordionActiveBgColor',
                            'Farbe des Balkens wenn ausgeklappt'
                        )}
                    </>
                )}

                {/* Textfarbe Balken - immer sichtbar */}
                {this.renderColorInput(
                    'Textfarbe im Balken',
                    config.accordionTextColor,
                    'accordionTextColor',
                    'Schriftfarbe und Icon-Farbe'
                )}

                {/* Info: Hintergrund = Website-Hintergrund */}
                <div className="dc-info-box">
                    <FiInfo size={16} className="dc-info-icon" />
                    <p>
                        Der Hintergrund des ausgeklappten Bereichs verwendet
                        automatisch die Website-Hintergrundfarbe.
                    </p>
                </div>
            </div>
        );
    }

    renderColorsTab = () => {
        const { config } = this.state;
        return (
            <div className="dc-tab-content dc-fade-in">
                {/* Website Hintergrund */}
                <div className="dc-section-header">
                    <h3 className="dc-section-title">Hintergrund</h3>
                    <p className="dc-section-description">Hintergrundfarbe der gesamten Website.</p>
                </div>

                {this.renderColorInput('Website-Hintergrund', config.websiteBackgroundColor, 'websiteBackgroundColor')}

                {/* Buttons */}
                <div className="dc-section-header" style={{ marginTop: '24px' }}>
                    <h3 className="dc-section-title">Schaltflächen</h3>
                    <p className="dc-section-description">Farben für alle Schaltflächen und deren Symbole.</p>
                </div>

                {this.renderColorInput(
                    'Schaltfläche Hintergrund',
                    config.buttonBgColor,
                    'buttonBgColor',
                    'Hintergrundfarbe aller Schaltflächen (z.B. Diagramm-Auswahl)'
                )}

                {this.renderColorInput(
                    'Schaltfläche Hover',
                    config.buttonHoverColor,
                    'buttonHoverColor',
                    'Hintergrundfarbe wenn die Maus über der Schaltfläche ist'
                )}

                {this.renderColorInput(
                    'Schaltfläche Text & Symbole',
                    config.buttonTextColor,
                    'buttonTextColor',
                    'Farbe für Beschriftungen und Symbole in Schaltflächen'
                )}

                {/* Button-Vorschau */}
                <div className="dc-button-preview" style={{ marginTop: '16px' }}>
                    <span className="dc-input-label">Vorschau</span>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                            type="button"
                            style={{
                                backgroundColor: config.buttonBgColor,
                                color: config.buttonTextColor,
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            <FiCheck size={16} />
                            <span>Button</span>
                        </button>
                        <button
                            type="button"
                            style={{
                                backgroundColor: config.buttonHoverColor,
                                color: config.buttonTextColor,
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                            title="Hover-Zustand"
                        >
                            <FiMousePointer size={16} />
                            <span>Hover</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderTabsTab = () => {
        const { config } = this.state;
        const showBorderColor = styleUsesBorderColor(config.tabStyle);

        return (
            <div className="dc-tab-content dc-fade-in">
                <div className="dc-section-header">
                    <h3 className="dc-section-title">Registerkarten</h3>
                    <p className="dc-section-description">
                        Stil und Farben für alle Tab-Ebenen
                    </p>
                </div>

                {/* Tab-Style Auswahl */}
                <div className="dc-input-card">
                    <label className="dc-input-label">Tab-Stil (gilt für alle Ebenen)</label>
                    <select
                        className="dc-select"
                        value={config.tabStyle}
                        onChange={(e) => this.updateConfig('tabStyle', e.target.value)}
                    >
                        {tabStyles.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Akzentfarbe - nur anzeigen wenn der Style diese verwendet */}
                {showBorderColor && this.renderColorInput(
                    'Akzentfarbe (Rahmen)',
                    config.tabBorderColor,
                    'tabBorderColor',
                    'Für Unterstreichungen und Rahmen'
                )}

                {/* Ebenenspezifische Einstellungen */}
                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Farben pro Ebene</h4>
                    <small className="dc-helper-text">Klicken Sie auf eine Ebene, um deren Farben anzupassen</small>
                </div>

                <div className="dc-levels-accordion">
                    {this.renderTabLevelSection('tabTop')}
                    {this.renderTabLevelSection('tabMiddle')}
                    {this.renderTabLevelSection('tabBottom')}
                    {this.renderTabLevelSection('tabVeryBottom')}
                </div>

                {/* Info-Box */}
                <div className="dc-info-box">
                    <FiInfo className="dc-info-icon" />
                    <p>Jede Navigationsebene kann eigene Farben haben. Verwenden Sie "Auf alle übertragen" um eine Ebene als Vorlage zu nutzen.</p>
                </div>
            </div>
        );
    }

    renderTexteTab = () => {
        const { config } = this.state;

        // Inline-Style für Schriftart-Vorschau mit CSS Custom Property
        // die CSS-Variable --dc-preview-font wird vom CSS mit !important verwendet
        const fontPreviewStyle: React.CSSProperties & { [key: string]: string } = {
            '--dc-preview-font': `'${config.fontFamily}', Arial, sans-serif`,
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginTop: '12px'
        } as React.CSSProperties & { [key: string]: string };

        return (
            <div className="dc-tab-content dc-fade-in">
                <div className="dc-section-header">
                    <h3 className="dc-section-title">Schriften</h3>
                    <p className="dc-section-description">Schriftart für die gesamte Website.</p>
                </div>

                {this.renderSelect('Schriftart', config.fontFamily, 'fontFamily', fontFamilies)}

                <div className="dc-font-preview" style={fontPreviewStyle}>
                    <span style={{ fontSize: '18px', display: 'block', marginBottom: '8px' }}>
                        Vorschau: {config.fontFamily}
                    </span>
                    <span style={{ fontSize: '14px', display: 'block' }}>
                        ABCDEFGHIJKLMNOPQRSTUVWXYZ
                    </span>
                    <span style={{ fontSize: '14px', display: 'block' }}>
                        abcdefghijklmnopqrstuvwxyz 0123456789
                    </span>
                </div>
            </div>
        );
    }

    // Sektion im Tab "Vorlagen & Versionen": Live-Design auf andere Gemeinden des Kunden übertragen
    renderTransferSection = () => {
        const {
            customerGemeinden, selectedTransferGemeinden, isLoadingGemeinden,
            isTransferring, transferDropdownOpen, currentLiveVersion
        } = this.state;

        const hasLiveDesign = currentLiveVersion !== null;
        const selectedCount = selectedTransferGemeinden.length;
        const allSelected = customerGemeinden.length > 0
            && customerGemeinden.every(g => selectedTransferGemeinden.includes(g.gemeinde));

        return (
            <>
                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Auf andere Gemeinden übertragen</h4>
                </div>

                {!hasLiveDesign ? (
                    <div className="dc-info-box">
                        <FiInfo size={16} className="dc-info-icon" />
                        <p>Schalten Sie zuerst ein Design live, um es auf andere Gemeinden übertragen zu können.</p>
                    </div>
                ) : isLoadingGemeinden ? (
                    <div className="dc-loading"><div className="dc-spinner-small" /></div>
                ) : customerGemeinden.length === 0 ? (
                    <div className="dc-empty-state-small">Es sind keine weiteren Gemeinden vorhanden.</div>
                ) : (
                    <>
                        <p className="dc-section-description dc-transfer-intro">
                            Übertragen Sie das aktuell live geschaltete Design auf weitere Gemeinden.
                            Es wird dort als neue Version gespeichert und live geschaltet.
                        </p>

                        <div className="dc-transfer-dropdown">
                            <button
                                type="button"
                                className={`dc-transfer-dropdown-trigger ${transferDropdownOpen ? 'active' : ''}`}
                                onClick={() => this.setState({ transferDropdownOpen: !transferDropdownOpen })}
                            >
                                <span>
                                    {selectedCount === 0
                                        ? 'Gemeinden auswählen'
                                        : `${selectedCount} ${selectedCount === 1 ? 'Gemeinde' : 'Gemeinden'} ausgewählt`}
                                </span>
                                {transferDropdownOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                            </button>

                            {transferDropdownOpen && (
                                <div className="dc-transfer-dropdown-panel">
                                    <label className="dc-transfer-option dc-transfer-option-all">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={this.toggleAllTransferGemeinden}
                                        />
                                        <span className="dc-transfer-option-name">Alle auswählen</span>
                                    </label>
                                    <div className="dc-transfer-option-list">
                                        {customerGemeinden.map(g => (
                                            <label key={g.gemeinde} className="dc-transfer-option">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTransferGemeinden.includes(g.gemeinde)}
                                                    onChange={() => this.toggleTransferGemeinde(g.gemeinde)}
                                                />
                                                <span className="dc-transfer-option-name">{g.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            className="dc-btn-primary dc-btn-transfer"
                            disabled={selectedCount === 0 || isTransferring}
                            onClick={this.handleTransferDesign}
                        >
                            {isTransferring ? <span className="dc-spinner" /> : <FiShare2 size={16} />}
                            <span>Übertragen &amp; live schalten</span>
                        </button>

                        <div className="dc-info-box">
                            <FiInfo size={16} className="dc-info-icon" />
                            <p>Ein bereits live geschaltetes Design der Ziel-Gemeinden wird ersetzt. Zum Entfernen rufen Sie den Designer für die jeweilige Gemeinde auf.</p>
                        </div>
                    </>
                )}
            </>
        );
    }

    // Auffälliger Button (nur für Superadmins) zum Öffnen des Zuordnungsdialogs
    renderSuperadminButton = () => {
        if (!this.canUseExternTransfer()) return null;

        return (
            <button
                type="button"
                className="dc-superadmin-btn"
                onClick={() => this.setState({ externDialogOpen: true })}
            >
                <FiUsers size={16} />
                <span>Design anderem Kunden übertragen</span>
                <span className="dc-superadmin-badge">Superadmin</span>
            </button>
        );
    }

    // Öffnet den Superadmin-Zuordnungsdialog (eigene Komponente DesignConfiguratorSA)
    renderExternDialog = () => {
        return (
            <DesignConfiguratorSA
                kunde={this.props.kunde}
                gemeinde={this.props.gemeinde}
                jahr={this.props.jahr}
                authToken={this.props.authToken}
                versions={this.state.availableVersions}
                templates={this.state.designTemplates}
                onClose={() => this.setState({ externDialogOpen: false })}
                onSuccess={(anzahl) => {
                    this.setState({
                        externDialogOpen: false,
                        successMessage: `Design auf ${this.gemeindeAnzahlWort(anzahl)} ${anzahl === 1 ? 'Gemeinde' : 'Gemeinden'} übertragen und live geschaltet.`
                    });
                    setTimeout(() => this.setState({ successMessage: null }), 5000);
                }}
            />
        );
    }

    renderVorlagenTab = () => {
        const { availableVersions, selectedVersion, selectedTemplateId, isLoadingVersions, designTemplates, isLoadingTemplates, isSaving, workingConfig, originalConfig } = this.state;

        const hasUnsavedChanges = JSON.stringify(workingConfig) !== JSON.stringify(originalConfig);

        // Hilfsfunktion: Header-Hintergrund berechnen
        const getHeaderBackground = (cfg: any): string => {
            if (cfg.headerGradientEnabled) {
                return `linear-gradient(${cfg.headerGradientAngle || 90}deg, ${cfg.headerBgColor || '#fff'}, ${cfg.headerGradientColor || '#fff'})`;
            } else if (cfg.headerGradientToWhite) {
                return `linear-gradient(to right, ${cfg.headerBgColor || '#fff'}, #FFFFFF, ${cfg.headerBgColor || '#fff'})`;
            }
            return cfg.headerBgColor || '#fff';
        };

        // Hilfsfunktion: Farbvorschau rendern
        const renderColorPreview = (cfg: any) => (
            <div className="dc-version-preview-mini">
                <div className="dc-preview-item">
                    <span className="dc-preview-label-small">Header:</span>
                    <span
                        className="dc-color-preview-box"
                        style={{ background: getHeaderBackground(cfg) }}
                        title={cfg.headerGradientEnabled ? 'Eigener Farbverlauf' : cfg.headerGradientToWhite ? 'Klassischer Gradient' : 'Einfarbig'}
                    />
                </div>
                <div className="dc-preview-item">
                    <span className="dc-preview-label-small">Hintergrund:</span>
                    <span
                        className="dc-color-preview-box"
                        style={{ backgroundColor: cfg.websiteBackgroundColor || '#f5f5f5' }}
                    />
                </div>
            </div>
        );

        return (
            <div className="dc-tab-content dc-fade-in">
                <div className="dc-section-header">
                    <h3 className="dc-section-title">Versionen & Vorlagen</h3>
                    <p className="dc-section-description">Verwalten Sie gespeicherte Versionen.</p>
                </div>

                {hasUnsavedChanges && (
                    <>
                        <div className="dc-subsection">
                            <h4 className="dc-subsection-title">Ungespeicherte Version</h4>
                        </div>

                        <div className="dc-version-item dc-version-draft">
                            <div className="dc-version-header">
                                <div className="dc-version-title">
                                    <strong>Entwurf</strong>
                                    <span className="dc-badge dc-badge-warning">● ENTWURF</span>
                                </div>
                            </div>

                            <div className="dc-draft-warning">
                                <FiAlertCircle size={14} />
                                <span>Ihre Änderungen sind noch nicht gespeichert.</span>
                            </div>

                            {renderColorPreview(workingConfig)}
                        </div>
                    </>
                )}

                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Gespeicherte Versionen</h4>
                </div>

                {isLoadingVersions ? (
                    <div className="dc-loading"><div className="dc-spinner-small" /></div>
                ) : availableVersions.length === 0 ? (
                    <div className="dc-empty-state-small">Noch keine Versionen vorhanden.</div>
                ) : (
                    <div className="dc-version-list">
                        {availableVersions.map(version => {
                            const isLive = version.istLive;
                            const isSelected = version.version === selectedVersion;

                            // Konfiguration parsen für Vorschau
                            let versionConfig: any = { ...defaultDesignConfig };
                            try {
                                if (version.konfigurationJson) {
                                    versionConfig = this.parseConfig(version.konfigurationJson);
                                }
                            } catch (e) {
                                console.warn('Fehler beim Parsen der Versions-Konfiguration:', e);
                            }

                            return (
                                <div key={version.version} className={`dc-version-item ${isLive ? 'dc-version-live' : ''} ${isSelected ? 'dc-version-selected' : ''}`}>
                                    <div className="dc-version-header">
                                        <div className="dc-version-title">
                                            <strong>Version {version.version}</strong>
                                            {isLive && <span className="dc-badge dc-badge-success">● LIVE</span>}
                                        </div>
                                        <span className="dc-version-date">{new Date(version.geaendertAm).toLocaleString('de-DE')}</span>
                                    </div>

                                    {renderColorPreview(versionConfig)}

                                    <div className="dc-version-actions">
                                        <button
                                            className={`dc-btn-small ${isSelected ? 'dc-btn-small-active' : ''}`}
                                            onClick={() => this.handleVersionPreview(version.version)}
                                            disabled={isSaving}
                                        >
                                            {isSelected ? '✓ Anwenden' : 'Anwenden'}
                                        </button>
                                        <button
                                            className="dc-btn-small dc-btn-small-danger"
                                            onClick={() => this.handleDeleteVersion(version.version, isLive)}
                                            disabled={isSaving}
                                            title={isLive ? "Live-Version löschen" : "Version löschen"}
                                        >
                                            <FiTrash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {this.renderTransferSection()}

                {this.renderSuperadminButton()}

                <div className="dc-subsection">
                    <h4 className="dc-subsection-title">Vorlagen</h4>
                </div>

                {isLoadingTemplates ? (
                    <div className="dc-loading"><div className="dc-spinner-small" /></div>
                ) : designTemplates.length === 0 ? (
                    <div className="dc-empty-state-small">Keine Vorlagen verfügbar</div>
                ) : (
                    <div className="dc-template-list">
                        {designTemplates.map(template => {
                            const isSelected = template.id === selectedTemplateId;

                            // Template-Konfiguration parsen für Vorschau
                            let templateConfig: any = { ...defaultDesignConfig };
                            try {
                                if (template.konfigurationJson) {
                                    templateConfig = this.parseConfig(template.konfigurationJson);
                                }
                            } catch (e) {
                                console.warn('Fehler beim Parsen der Template-Konfiguration:', e);
                            }

                            return (
                                <div key={template.id} className={`dc-template-item ${isSelected ? 'dc-template-selected' : ''}`}>
                                    <div className="dc-template-header">
                                        <strong>{template.name}</strong>
                                    </div>
                                    {template.beschreibung && <small className="dc-template-desc">{template.beschreibung}</small>}

                                    {renderColorPreview(templateConfig)}

                                    <div className="dc-template-actions">
                                        <button
                                            className={`dc-btn-small ${isSelected ? 'dc-btn-small-active' : ''}`}
                                            onClick={() => this.handleTemplatePreview(template)}
                                            disabled={isSaving}
                                        >
                                            {isSelected ? '✓ Anwenden' : 'Anwenden'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // ============================================================
    // MAIN RENDER
    // ============================================================

    render() {
        const { children } = this.props;
        const { activeTab, isSaving, error, successMessage, pendingAction } = this.state;
        const { loadingVisible, loadingFadeOut } = this.state;
        const splitscreenClass = `dc-splitscreen${loadingVisible && !loadingFadeOut ? ' dc-splitscreen--loading' : ''}`;

        return (
            <div className={splitscreenClass}>
                {loadingVisible && (
                    <div className={`dc-loading-overlay${loadingFadeOut ? ' dc-loading-overlay--out' : ''}`}>
                        <img src={this.logoSrc} alt="KSLPlus" className="dc-loading-logo" />
                    </div>
                )}
                {pendingAction && this.renderConfirmationDialog()}
                {this.state.externDialogOpen && this.renderExternDialog()}

                <header className="dc-header">
                    <div className="dc-header-left">
                        <img
                            src="https://kslplusdgest.z1.web.core.windows.net//0/1/2021/Bilder/KSL_plus 450x213.png"
                            alt="KSLPlus Logo"
                            className="dc-logo-image"
                        />
                        <div className="dc-header-titles">
                            <h1 className="dc-main-title">KSLPlus Designer</h1>
                            <p className="dc-subtitle">KSL SoftWerk GmbH</p>
                        </div>
                    </div>
                    <div className="dc-header-right">
                        {error && (
                            <div className="dc-alert dc-alert-error">
                                <FiX className="dc-alert-icon" onClick={() => this.setState({ error: null })} style={{ cursor: 'pointer' }} /> {error}
                            </div>
                        )}
                        {successMessage && (
                            <div className="dc-alert dc-alert-success">
                                <FiCheck className="dc-alert-icon" /> {successMessage}
                            </div>
                        )}
                        <div className="dc-header-divider" />
                        <button onClick={this.handleClose} disabled={isSaving} className="dc-close-btn" title="Schließen" type="button">
                            <FiX size={20} />
                        </button>
                    </div>
                </header>

                <div className="dc-main-content">
                    <aside className="dc-sidebar">
                        <nav className="dc-nav">
                            <div className="dc-nav-section-label">Einstellungen</div>
                            {this.renderNavButton('header', <FiLayout />, 'Header')}
                            {this.props.hasHauptText && this.renderNavButton('accordion', <FiChevronDown />, 'Begrüßungstext')}
                            {this.renderNavButton('colors', <FiDroplet />, 'Hintergrund & Schaltflächen')}
                            {this.renderNavButton('tabs', <FiLayers />, 'Registerkarten')}
                            {this.renderNavButton('texte', <FiType />, 'Schriften')}
                            <div className="dc-nav-section-label dc-nav-section-spacer">System</div>
                            {this.renderNavButton('vorlagen', <FiClock />, 'Vorlagen & Versionen')}
                        </nav>

                        <div className="dc-config-content">
                            {activeTab === 'header' && this.renderHeaderTab()}
                            {/* Accordion-Tab nur rendern wenn hasHauptText */}
                            {activeTab === 'accordion' && this.props.hasHauptText && this.renderAccordionTab()}
                            {activeTab === 'colors' && this.renderColorsTab()}
                            {activeTab === 'tabs' && this.renderTabsTab()}
                            {activeTab === 'texte' && this.renderTexteTab()}
                            {activeTab === 'vorlagen' && this.renderVorlagenTab()}
                        </div>

                        <div className="dc-actions">
                            <button onClick={this.handleSaveAndActivate} disabled={isSaving} className="dc-btn-primary dc-btn-lg" type="button">
                                {isSaving ? <span className="dc-spinner" /> : <FiSave size={18} />}
                                <span>Speichern & Anwenden</span>
                            </button>
                            <div className="dc-secondary-actions">
                                <button onClick={this.handleSave} disabled={isSaving} className="dc-btn-secondary" type="button">
                                    <FiSave size={14} />
                                    <span>Nur Speichern</span>
                                </button>
                                <button onClick={this.handleReset} className="dc-btn-secondary" type="button">
                                    <FiRefreshCw size={14} />
                                    <span>Zurückstellen</span>
                                </button>
                            </div>
                        </div>
                    </aside>

                    <main className="dc-preview-panel">
                        <div className="dc-preview-header">
                            <div className="dc-preview-indicator">
                                <span className="dc-live-dot">
                                    <span className="dc-live-dot-ping" />
                                    <span className="dc-live-dot-core" />
                                </span>
                                <span className="dc-preview-label-text">Live Preview</span>
                            </div>
                            <div className="dc-preview-info">
                                <FiClock className="dc-preview-info-icon" />
                                <span>Echtzeit-Aktualisierung</span>
                            </div>
                        </div>
                        <div className="dc-preview-area">
                            <div className="dc-preview-frame">
                                <div className="dc-preview-content">
                                    {children}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default DesignConfigurator;